import React, { useState, useEffect, useMemo } from 'react';
import {
  Table, Button, Space, Card, message, Tabs, Popconfirm, Select, DatePicker,
  Modal, Form, Input, Tag, Row, Col, InputNumber, TimePicker, Progress, Spin, Empty, AutoComplete, Alert, Upload
} from 'antd';
import {
  CalendarOutlined, PlusOutlined, EditOutlined,
  DeleteOutlined, DownloadOutlined, UploadOutlined,
  BarChartOutlined, ClearOutlined, FilePdfOutlined, TableOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useRealTimeUpdates } from '../../../../Hooks/useRealTime';

import { fetchClusters, fetchAllClusterStandards } from '../../../../Service/Clusters_API/clustersApi';
import { fetchInstructors } from '../../../../Service/Instructor_API/instructorApi';
import { fetchStaff } from '../../../../Service/Staff_API/staffApi';
import {
  fetchAcademicYears,
  fetchStandards,
  fetchSubjects,
  fetchLessons,
  fetchDailySessions, createDailySession, updateDailySession, deleteDailySession, bulkUploadDailySessions
} from '../../../../Service/Syllabus_API/syllabusApi';

// Robust RFC-4180 compliant CSV Parser
const parseCSVText = (text) => {
  const result = [];
  let row = [];
  let entry = '';
  let insideQuote = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const nextChar = text[i + 1];

    if (char === '"') {
      if (insideQuote && nextChar === '"') {
        entry += '"';
        i++; // skip escaped quote
      } else {
        insideQuote = !insideQuote;
      }
    } else if (char === ',' && !insideQuote) {
      row.push(entry.trim());
      entry = '';
    } else if ((char === '\r' || char === '\n') && !insideQuote) {
      if (char === '\r' && nextChar === '\n') {
        i++; // skip \r\n
      }
      row.push(entry.trim());
      entry = '';
      if (row.some(cell => cell.length > 0)) {
        result.push(row);
      }
      row = [];
    } else {
      entry += char;
    }
  }
  if (entry.length > 0 || row.length > 0) {
    row.push(entry.trim());
    if (row.some(cell => cell.length > 0)) {
      result.push(row);
    }
  }
  return result;
};

const { Option } = Select;

const formatTime12h = (timeStr) => {
  if (!timeStr) return '—';
  const parts = timeStr.split(':');
  if (parts.length >= 2) {
    const h = parseInt(parts[0], 10);
    const m = parts[1];
    const ampm = h >= 12 ? 'PM' : 'AM';
    const formattedHour = h % 12 || 12;
    return `${String(formattedHour).padStart(2, '0')}:${m} ${ampm}`;
  }
  const d = dayjs(timeStr);
  return d.isValid() ? d.format('hh:mm A') : timeStr;
};

const sortStandardsAscending = (stdList) => {
  if (!Array.isArray(stdList)) return [];
  return [...stdList].sort((a, b) => {
    const nameA = a.standard_name || `Standard #${a.standard_id}`;
    const nameB = b.standard_name || `Standard #${b.standard_id}`;
    return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
  });
};

const sortLessonsAscending = (lessonList) => {
  if (!Array.isArray(lessonList)) return [];
  return [...lessonList].sort((a, b) => {
    const nameA = a.lesson_name || `Lesson #${a.lessons_id || 0}`;
    const nameB = b.lesson_name || `Lesson #${b.lessons_id || 0}`;
    return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
  });
};

const formatLessonTopic = (part, lesson, emptyVal = '—') => {
  if (part && lesson) {
    if (String(part).toLowerCase().includes(String(lesson).toLowerCase())) return part;
    return `${lesson} - ${part}`;
  }
  return part || lesson || emptyVal;
};

const getCurrentAcademicYear = (ayList) => {
  if (!Array.isArray(ayList) || ayList.length === 0) return null;
  return (
    ayList.find(y => y.is_current || y.is_active || String(y.is_current) === '1' || String(y.is_active) === '1') ||
    ayList.find(y => y.status === 'Active' || y.status === 'ACTIVE' || y.status?.toLowerCase() === 'active') ||
    ayList.find(y => {
      if (!y.start_date || !y.end_date) return false;
      const today = new Date();
      const start = new Date(y.start_date);
      const end = new Date(y.end_date);
      return !isNaN(start.getTime()) && !isNaN(end.getTime()) && today >= start && today <= end;
    }) ||
    ayList[0]
  );
};


const SessionManagementPage = () => {
  const userRole = localStorage.getItem('role') || 'USER';
  const isStudio = userRole === 'STUDIO';

  const selectSearchProps = {
    showSearch: true,
    optionFilterProp: 'children',
    filterOption: (input, option) => {
      if (!option || !option.children) return false;
      const cleanStr = Array.isArray(option.children)
        ? option.children.map(c => typeof c === 'object' ? '' : c).join('')
        : option.children.toString();
      return cleanStr.toLowerCase().includes(input.toLowerCase());
    }
  };
  const [activeTab, setActiveTab] = useState('session-control');
  
  // --- Global Filter States for Tab 1 (Session Control Matrix) ---
  const [clusters, setClusters] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [staffList, setStaffList] = useState([]);
  
  const [filterCluster, setFilterCluster] = useState(null);
  const [filterAcademicYear, setFilterAcademicYear] = useState(null);
  const [filterStandard, setFilterStandard] = useState(null);
  const [filterSubject, setFilterSubject] = useState(null);
  const [filterInstructor, setFilterInstructor] = useState(null);
  const [filterDate, setFilterDate] = useState(dayjs());

  // --- Daily Session Control State ---
  const [dailySessionsList, setDailySessionsList] = useState([]);
  const [academicYearSessionsList, setAcademicYearSessionsList] = useState([]);
  const [dailySessionsLoading, setDailySessionsLoading] = useState(false);
  const [clusterFilteredStandards, setClusterFilteredStandards] = useState([]);

  // --- Syllabus States ---
  const [standards, setStandards] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [clusterStandardsList, setClusterStandardsList] = useState([]);

  // --- Syllabus Progress Tracker State for Tab 2 ---
  const [progressDailySessions, setProgressDailySessions] = useState([]);
  const [progressLoading, setProgressLoading] = useState(false);

  const [filterAyForProgress, setFilterAyForProgress] = useState(null);
  const [filterClusterForProgress, setFilterClusterForProgress] = useState(null);
  const [filterStdForProgress, setFilterStdForProgress] = useState(null);
  const [filterSubForProgress, setFilterSubForProgress] = useState(null);

  // --- Cascading Form State ---
  const [modalType, setModalType] = useState(null); // 'daily_session'
  const [editingRecord, setEditingRecord] = useState(null);
  const [formAcademicYearId, setFormAcademicYearId] = useState(null);
  const [formClusterId, setFormClusterId] = useState(null);
  const [formClusterStandards, setFormClusterStandards] = useState([]);
  const [formStandardId, setFormStandardId] = useState(null);
  const [formSubjectId, setFormSubjectId] = useState(null);
  const [form] = Form.useForm();
  const [modalLoading, setModalLoading] = useState(false);
  const [isLessonAlreadyCompleted, setIsLessonAlreadyCompleted] = useState(false);
  const [completedLessonInfo, setCompletedLessonInfo] = useState(null);

  // --- CSV Bulk Upload State ---
  const [isCsvModalOpen, setIsCsvModalOpen] = useState(false);
  const [csvParsedData, setCsvParsedData] = useState([]);
  const [uploadingCsv, setUploadingCsv] = useState(false);

  useEffect(() => {
    loadInitialFilters();
  }, []);

  const loadAllData = (silent = false) => {
    loadDailySessions(silent);
    loadProgressData(silent);
  };

  useRealTimeUpdates('daily_session', () => loadAllData(true));
  useRealTimeUpdates('cluster_standard', () => loadAllData(true));

  // Reload daily sessions when Tab 1/2 filters change
  useEffect(() => {
    if (activeTab === 'session-control' || activeTab === 'teacher-matrix') {
      loadDailySessions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filterCluster, filterAcademicYear, filterStandard, filterSubject, filterInstructor, filterDate]);

  // Reload progress data when Tab 3 filters change
  useEffect(() => {
    if (activeTab === 'syllabus-progress') {
      loadProgressData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filterAyForProgress, filterClusterForProgress]);

  const loadInitialFilters = async () => {
    try {
      const [clusData, ayData, instData, staffData, stdData, subData, lesData, csData] = await Promise.all([
        fetchClusters().catch(() => []),
        fetchAcademicYears().catch(() => []),
        fetchInstructors().catch(() => []),
        fetchStaff().catch(() => []),
        fetchStandards().catch(() => []),
        fetchSubjects().catch(() => []),
        fetchLessons().catch(() => []),
        fetchAllClusterStandards().catch(() => [])
      ]);
      setClusters(clusData || []);
      setAcademicYears(ayData || []);
      setInstructors(instData || []);
      setStaffList(staffData || []);
      setStandards(stdData || []);
      setSubjects(subData || []);
      setLessons(lesData || []);
      setClusterStandardsList(csData || []);

      const activeAy = getCurrentAcademicYear(ayData || []);
      if (activeAy) {
        setFilterAcademicYear(activeAy.academic_year_id);
        setFilterAyForProgress(activeAy.academic_year_id);
      }
    } catch (err) {
      console.error('Failed to load initial filters:', err);
    }
  };

  const loadDailySessions = async (silent = false) => {
    if (!silent) setDailySessionsLoading(true);
    try {
      const filters = {};
      if (filterCluster) filters.cluster_id = filterCluster;
      if (filterAcademicYear) filters.academic_year_id = filterAcademicYear;
      if (filterStandard) filters.standard_id = filterStandard;
      if (filterSubject) filters.subjects_id = filterSubject;
      if (filterInstructor) filters.instructor_id = filterInstructor;
      
      const allSessions = await fetchDailySessions(filters);
      setAcademicYearSessionsList(allSessions || []);
      
      let filtered = allSessions || [];
      if (filterDate) {
        const monthStr = filterDate.format('YYYY-MM');
        filtered = filtered.filter(ds => {
          if (!ds.daily_session_date) return false;
          return dayjs(ds.daily_session_date).format('YYYY-MM') === monthStr;
        });
      }

      setDailySessionsList(filtered);
    } catch (err) {
      console.error('Failed to load daily sessions:', err);
      if (!silent) message.error('Failed to fetch daily session matrix records.');
    } finally {
      if (!silent) setDailySessionsLoading(false);
    }
  };

  const loadProgressData = async (silent = false) => {
    if (!silent) setProgressLoading(true);
    try {
      const filters = {};
      if (filterAyForProgress) filters.academic_year_id = filterAyForProgress;
      if (filterClusterForProgress) filters.cluster_id = filterClusterForProgress;

      const [stdRes, subRes, lesRes, dsRes, ayRes, clusRes, csRes] = await Promise.all([
        fetchStandards().catch(() => []),
        fetchSubjects().catch(() => []),
        fetchLessons().catch(() => []),
        fetchDailySessions(filters).catch(() => []),
        fetchAcademicYears().catch(() => []),
        fetchClusters().catch(() => []),
        fetchAllClusterStandards(filterAyForProgress).catch(() => [])
      ]);

      if (stdRes && Array.isArray(stdRes)) setStandards(stdRes);
      if (subRes && Array.isArray(subRes)) setSubjects(subRes);
      if (lesRes && Array.isArray(lesRes)) setLessons(lesRes);
      if (dsRes && Array.isArray(dsRes)) setProgressDailySessions(dsRes);
      if (ayRes && Array.isArray(ayRes)) setAcademicYears(ayRes);
      if (clusRes && Array.isArray(clusRes)) setClusters(clusRes);
      if (csRes && Array.isArray(csRes)) setClusterStandardsList(csRes);
    } catch (err) {
      console.error('Failed to load progress data:', err);
    } finally {
      if (!silent) setProgressLoading(false);
    }
  };

  // --- Teacher vs Standard Matrix Calculation (Grouped by Cluster) ---
  const matrixGroups = useMemo(() => {
    const calculateForCluster = (clusterId, explicitClusterStandards) => {
      let relStandards = standards;

      if (clusterId && explicitClusterStandards && explicitClusterStandards.length > 0) {
        relStandards = explicitClusterStandards;
      } else if (clusterId) {
        const linkedStdIds = new Set(
          clusterStandardsList
            .filter(cs => String(cs.cluster_id) === String(clusterId))
            .map(cs => String(cs.standard_id))
        );
        const sessionStdIds = new Set(
          dailySessionsList
            .filter(ds => String(ds.cluster_id) === String(clusterId))
            .map(ds => String(ds.standard_id))
            .filter(Boolean)
        );
        if (linkedStdIds.size > 0 || sessionStdIds.size > 0) {
          relStandards = standards.filter(s =>
            linkedStdIds.has(String(s.standard_id)) ||
            sessionStdIds.has(String(s.standard_id)) ||
            String(s.cluster_id) === String(clusterId)
          );
        }
      }

      if (filterAcademicYear) {
        relStandards = relStandards.filter(s => !s.academic_year_id || String(s.academic_year_id) === String(filterAcademicYear));
      }

      if (filterStandard) {
        relStandards = relStandards.filter(s => String(s.standard_id) === String(filterStandard));
      }

      const sortedStds = sortStandardsAscending(relStandards);
      const clusterStdIdsSet = new Set(sortedStds.map(s => String(s.standard_id)));

      const teacherMap = {};
      instructors.forEach(inst => {
        teacherMap[inst.instructor_id] = {
          instructor_id: inst.instructor_id,
          instructor_name: inst.instructor_name,
          counts: {},
          liveCounts: {},
          recordedCounts: {}
        };
        sortedStds.forEach(std => {
          teacherMap[inst.instructor_id].counts[std.standard_id] = 0;
          teacherMap[inst.instructor_id].liveCounts[std.standard_id] = 0;
          teacherMap[inst.instructor_id].recordedCounts[std.standard_id] = 0;
        });
      });

      let totalClassCount = 0;
      let totalLiveCount = 0;
      let totalRecordedCount = 0;
      let totalCancelledCount = 0;

      // Filter dailySessions STRICTLY for this specific cluster
      const relevantSessions = clusterId
        ? dailySessionsList.filter(ds => {
            if (ds.cluster_id !== undefined && ds.cluster_id !== null && String(ds.cluster_id) !== '') {
              return String(ds.cluster_id) === String(clusterId);
            }
            return clusterStdIdsSet.has(String(ds.standard_id));
          })
        : dailySessionsList;

      relevantSessions.forEach(ds => {
        const isCancelled = ds.session_type === 'CANCELLED';
        const isRecorded = ds.session_type === 'RECORDED';
        const count = Number(ds.session_count || 1);

        if (isCancelled) {
          totalCancelledCount += count;
        } else if (isRecorded) {
          // RECORDED sessions are tracked separately, NOT counted in main matrix
          totalRecordedCount += count;
          if (ds.instructor_id && teacherMap[ds.instructor_id]) {
            const stdId = ds.standard_id;
            if (stdId !== undefined && stdId !== null) {
              if (teacherMap[ds.instructor_id].recordedCounts[stdId] !== undefined) {
                teacherMap[ds.instructor_id].recordedCounts[stdId] += count;
              }
            }
          }
        } else {
          // Only LIVE sessions count in the main matrix
          totalLiveCount += count;
          totalClassCount += count;
          if (ds.instructor_id && teacherMap[ds.instructor_id]) {
            const stdId = ds.standard_id;
            if (stdId !== undefined && stdId !== null) {
              if (teacherMap[ds.instructor_id].counts[stdId] !== undefined) {
                teacherMap[ds.instructor_id].counts[stdId] += count;
                teacherMap[ds.instructor_id].liveCounts[stdId] += count;
              }
            }
          }
        }
      });

      const stdSubtotals = {};
      const stdLiveSubtotals = {};
      const stdRecordedSubtotals = {};

      sortedStds.forEach(std => {
        let liveSum = 0;
        let recSum = 0;
        Object.values(teacherMap).forEach(t => {
          liveSum += t.liveCounts[std.standard_id] || 0;
          recSum += t.recordedCounts[std.standard_id] || 0;
        });
        stdSubtotals[std.standard_id] = liveSum; // subtotal = live only
        stdLiveSubtotals[std.standard_id] = liveSum;
        stdRecordedSubtotals[std.standard_id] = recSum;
      });

      // teacherRows filter based on live counts only
      let teacherRows = Object.values(teacherMap).filter(t => {
        const liveSum = Object.values(t.liveCounts).reduce((a, b) => a + b, 0);
        const recSum = Object.values(t.recordedCounts).reduce((a, b) => a + b, 0);
        return liveSum > 0 || recSum > 0 || (instructors.length < 15 && !clusterId);
      });

      if (filterInstructor) {
        teacherRows = teacherRows.filter(t => String(t.instructor_id) === String(filterInstructor));
      }

      const relLessons = filterAcademicYear
        ? lessons.filter(l => {
            if (l.academic_year_id && String(l.academic_year_id) !== String(filterAcademicYear)) return false;
            const sub = subjects.find(s => String(s.subjects_id) === String(l.subjects_id));
            if (sub && sub.academic_year_id && String(sub.academic_year_id) !== String(filterAcademicYear)) return false;
            return true;
          })
        : lessons;

      const clusterRelLessons = relLessons.filter(l => {
        if (l.standard_id && clusterStdIdsSet.has(String(l.standard_id))) return true;
        const sub = subjects.find(s => String(s.subjects_id) === String(l.subjects_id));
        return sub && clusterStdIdsSet.has(String(sub.standard_id));
      });

      const totalAllocatedSessions = clusterRelLessons.reduce((acc, l) => acc + (Number(l.lesson_total_sessions) || 1), 0);

      // Academic Year total live sessions conducted for this cluster (independent of month filter)
      const clusterYearSessions = clusterId
        ? (academicYearSessionsList || []).filter(ds => {
            if (ds.cluster_id !== undefined && ds.cluster_id !== null && String(ds.cluster_id) !== '') {
              return String(ds.cluster_id) === String(clusterId);
            }
            return clusterStdIdsSet.has(String(ds.standard_id));
          })
        : (academicYearSessionsList || []);

      const totalYearLiveCount = clusterYearSessions
        .filter(ds => ds.session_type !== 'CANCELLED' && ds.session_type !== 'RECORDED')
        .reduce((acc, ds) => acc + Number(ds.session_count || 1), 0);

      // Total Remaining Sessions for the Academic Year (NOT by month)
      const remainingClass = Math.max(0, totalAllocatedSessions - totalYearLiveCount);

      return {
        sortedStds,
        teacherRows,
        stdSubtotals,
        stdLiveSubtotals,
        stdRecordedSubtotals,
        totalLiveCount,
        totalRecordedCount,
        totalClassCount,
        totalYearLiveCount,
        totalAllocatedSessions,
        remainingClass,
        totalCancelledCount
      };
    };

    if (filterCluster) {
      const clusterObj = clusters.find(c => String(c.cluster_id) === String(filterCluster));
      return [{
        clusterId: filterCluster,
        clusterName: clusterObj?.cluster_name || 'Selected Cluster',
        data: calculateForCluster(filterCluster, clusterFilteredStandards)
      }];
    } else {
      const availableClustersForMatrix = filterAcademicYear
        ? clusters.filter(c => !c.academic_year_id || String(c.academic_year_id) === String(filterAcademicYear))
        : clusters;

      return availableClustersForMatrix.map(c => {
        const clusterStds = clusterStandardsList
          .filter(cs => String(cs.cluster_id) === String(c.cluster_id))
          .map(cs => standards.find(s => String(s.standard_id) === String(cs.standard_id)) || {
            standard_id: cs.standard_id,
            standard_name: cs.standard_name || `Standard #${cs.standard_id}`,
            academic_year_id: cs.academic_year_id
          })
          .filter(Boolean);

        return {
          clusterId: c.cluster_id,
          clusterName: c.cluster_name,
          data: calculateForCluster(c.cluster_id, clusterStds)
        };
      }).filter(mg => mg.data.sortedStds.length > 0)
      .sort((a, b) => (a.clusterName || '').localeCompare(b.clusterName || ''));
    }
  }, [dailySessionsList, academicYearSessionsList, instructors, standards, clusterFilteredStandards, lessons, subjects, filterAcademicYear, filterCluster, filterStandard, filterInstructor, clusters, clusterStandardsList]);

  // --- Progress Tracker Filtering & Progress Logic (Cluster-Wise) ---
  const availableClustersForProgress = filterAyForProgress
    ? clusters.filter(c => !c.academic_year_id || String(c.academic_year_id) === String(filterAyForProgress))
    : clusters;

  const availableStandardsForProgress = useMemo(() => {
    let stdList = filterAyForProgress
      ? standards.filter(s => !s.academic_year_id || String(s.academic_year_id) === String(filterAyForProgress))
      : standards;

    if (filterClusterForProgress) {
      const linkedStdIds = new Set(
        clusterStandardsList
          .filter(cs => String(cs.cluster_id) === String(filterClusterForProgress))
          .map(cs => String(cs.standard_id))
      );
      if (linkedStdIds.size > 0) {
        stdList = stdList.filter(s => linkedStdIds.has(String(s.standard_id)));
      }
    }
    return sortStandardsAscending(stdList);
  }, [standards, filterAyForProgress, filterClusterForProgress, clusterStandardsList]);

  const availableSubjectsForProgress = useMemo(() => {
    let clusterLinkedStdIds = null;
    if (filterClusterForProgress && !filterStdForProgress) {
      const linked = clusterStandardsList
        .filter(cs => String(cs.cluster_id) === String(filterClusterForProgress))
        .map(cs => String(cs.standard_id));
      if (linked.length > 0) clusterLinkedStdIds = new Set(linked);
    }

    return (subjects || []).filter(sub => {
      const matchAy = !filterAyForProgress || !sub.academic_year_id || String(sub.academic_year_id) === String(filterAyForProgress);
      const matchStd = filterStdForProgress
        ? String(sub.standard_id) === String(filterStdForProgress)
        : (!clusterLinkedStdIds || clusterLinkedStdIds.has(String(sub.standard_id)));
      return matchAy && matchStd;
    });
  }, [subjects, filterAyForProgress, filterStdForProgress, filterClusterForProgress, clusterStandardsList]);

  const getLessonProgressDataForCluster = (lessonId, clusterId) => {
    const matchingDs = progressDailySessions.filter(ds => {
      const matchLesson = String(ds.lessons_id) === String(lessonId);
      const matchClus = !clusterId || String(ds.cluster_id) === String(clusterId);
      return matchLesson && matchClus;
    });

    const usedSessions = matchingDs.reduce((acc, ds) => {
      const count = ds.session_count !== undefined && ds.session_count !== null ? Number(ds.session_count) : 1;
      return acc + count;
    }, 0);

    let status = '';
    for (let i = matchingDs.length - 1; i >= 0; i--) {
      if (matchingDs[i].lesson_status && String(matchingDs[i].lesson_status).trim() !== '') {
        status = String(matchingDs[i].lesson_status).trim();
        break;
      }
    }

    return { usedSessions, status };
  };

  const getSubjectProgressDataForCluster = (subjectId, clusterId) => {
    const subLessons = lessons.filter(l => String(l.subjects_id) === String(subjectId));
    const totalAllocated = subLessons.reduce((acc, l) => acc + (Number(l.lesson_total_sessions) || 1), 0);

    let totalUsed = 0;
    subLessons.forEach(l => {
      const { usedSessions } = getLessonProgressDataForCluster(l.lessons_id, clusterId);
      totalUsed += usedSessions;
    });

    const remaining = Math.max(0, totalAllocated - totalUsed);
    const percent = totalAllocated > 0 ? Math.min(100, Math.round((totalUsed / totalAllocated) * 100)) : 0;
    return { totalAllocated, totalUsed, remaining, percent, lessonCount: subLessons.length, subLessons };
  };

  // Grouped Progress Tracker Data by Cluster (Only checking standards linked to each cluster)
  const clusterProgressGroups = useMemo(() => {
    let targetClusters = availableClustersForProgress;
    if (filterClusterForProgress) {
      targetClusters = targetClusters.filter(c => String(c.cluster_id) === String(filterClusterForProgress));
    }

    if (!targetClusters || targetClusters.length === 0) {
      if (filterClusterForProgress) {
        const c = clusters.find(cl => String(cl.cluster_id) === String(filterClusterForProgress));
        if (c) targetClusters = [c];
      }
    }

    return targetClusters.map(c => {
      const cId = c.cluster_id;
      const cName = c.cluster_name || `Cluster #${cId}`;
      const cCode = c.cluster_code || '';
      const cAyId = c.academic_year_id;

      // 1. Get ONLY the standards explicitly linked to this cluster in cluster_standard mapping
      const linkedClusterStandards = clusterStandardsList.filter(cs => String(cs.cluster_id) === String(cId));
      let linkedStdIds = new Set(linkedClusterStandards.map(cs => String(cs.standard_id)));

      // Fallback: If no cluster_standard mapping rows are populated in DB for this cluster,
      // also check standards that have cluster_id === cId or daily sessions recorded for this cluster
      if (linkedStdIds.size === 0) {
        const dsStdIds = progressDailySessions
          .filter(ds => String(ds.cluster_id) === String(cId))
          .map(ds => String(ds.standard_id))
          .filter(Boolean);
        const stClusterIds = standards
          .filter(s => s.cluster_id && String(s.cluster_id) === String(cId))
          .map(s => String(s.standard_id));
        linkedStdIds = new Set([...dsStdIds, ...stClusterIds]);
      }

      // Filter subjects: ONLY include subjects whose standard is linked to this cluster
      const clusterSubjects = subjects.filter(sub => {
        if (!linkedStdIds.has(String(sub.standard_id))) return false;
        if (filterAyForProgress && sub.academic_year_id && String(sub.academic_year_id) !== String(filterAyForProgress)) return false;
        if (!filterAyForProgress && cAyId && sub.academic_year_id && String(sub.academic_year_id) !== String(cAyId)) return false;
        if (filterStdForProgress && String(sub.standard_id) !== String(filterStdForProgress)) return false;
        if (filterSubForProgress && String(sub.subjects_id) !== String(filterSubForProgress)) return false;
        return true;
      });

      let totalAllocatedSessions = 0;
      let totalUsedSessions = 0;
      let totalLessons = 0;

      const subjectsWithProgress = clusterSubjects.map(sub => {
        const stdObj = standards.find(s => String(s.standard_id) === String(sub.standard_id));
        const ayObj = academicYears.find(ay => String(ay.academic_year_id) === String(sub.academic_year_id || cAyId));
        const progress = getSubjectProgressDataForCluster(sub.subjects_id, cId);

        totalAllocatedSessions += progress.totalAllocated;
        totalUsedSessions += progress.totalUsed;
        totalLessons += progress.lessonCount;

        return {
          ...sub,
          standard_name: stdObj?.standard_name || sub.subjects_standard || '—',
          standard_medium: stdObj?.standard_medium || 'English',
          academic_year_name: ayObj?.academic_year || '—',
          progress
        };
      });

      const overallPercent = totalAllocatedSessions > 0
        ? Math.min(100, Math.round((totalUsedSessions / totalAllocatedSessions) * 100))
        : 0;

      const totalRemainingSessions = Math.max(0, totalAllocatedSessions - totalUsedSessions);

      return {
        clusterId: cId,
        clusterName: cName,
        clusterCode: cCode,
        academicYearName: academicYears.find(ay => String(ay.academic_year_id) === String(cAyId))?.academic_year || 'All Years',
        linkedStandardsCount: linkedStdIds.size,
        subjects: subjectsWithProgress,
        totalSubjects: subjectsWithProgress.length,
        totalLessons,
        totalAllocatedSessions,
        totalUsedSessions,
        totalRemainingSessions,
        overallPercent
      };
    })
    .filter(cg => cg.linkedStandardsCount > 0 || cg.subjects.length > 0)
    .sort((a, b) => (a.clusterName || '').localeCompare(b.clusterName || ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableClustersForProgress, filterClusterForProgress, filterAyForProgress, filterStdForProgress, filterSubForProgress, subjects, standards, academicYears, lessons, progressDailySessions, clusters, clusterStandardsList]);

  // Helper for available clusters based on selected Academic Year
  const availableClusters = useMemo(() => {
    if (!filterAcademicYear) return clusters;
    return clusters.filter(c => !c.academic_year_id || String(c.academic_year_id) === String(filterAcademicYear));
  }, [clusters, filterAcademicYear]);

  // Auto-default cluster filters for STUDIO role
  useEffect(() => {
    if (isStudio && availableClusters.length > 0) {
      if (!filterCluster || !availableClusters.some(c => c.cluster_id === filterCluster)) {
        setFilterCluster(availableClusters[0].cluster_id);
      }
    }
  }, [isStudio, availableClusters, filterCluster]);

  useEffect(() => {
    if (isStudio && availableClustersForProgress.length > 0) {
      if (!filterClusterForProgress || !availableClustersForProgress.some(c => c.cluster_id === filterClusterForProgress)) {
        setFilterClusterForProgress(availableClustersForProgress[0].cluster_id);
      }
    }
  }, [isStudio, availableClustersForProgress, filterClusterForProgress]);

  // Helper for available standards based on selected Academic Year and Cluster for Tab 1 Filter
  const availableStandardsForTab1 = useMemo(() => {
    let stdList = standards;
    if (filterCluster) {
      if (clusterFilteredStandards.length > 0) {
        stdList = clusterFilteredStandards;
      } else {
        const clusterStdIds = new Set(
          dailySessionsList
            .filter(ds => !ds.cluster_id || String(ds.cluster_id) === String(filterCluster))
            .map(ds => String(ds.standard_id))
            .filter(Boolean)
        );
        stdList = stdList.filter(s => clusterStdIds.has(String(s.standard_id)) || String(s.cluster_id) === String(filterCluster));
      }
    }
    if (filterAcademicYear) {
      stdList = stdList.filter(s => !s.academic_year_id || String(s.academic_year_id) === String(filterAcademicYear));
    }
    return sortStandardsAscending(stdList);
  }, [standards, filterAcademicYear, filterCluster, clusterFilteredStandards, dailySessionsList]);

  // Helper for available subjects based on selected Standard for Tab 1 Filter
  const availableSubjectsForTab1 = useMemo(() => {
    if (!filterStandard) return subjects;
    return subjects.filter(sub => String(sub.standard_id) === String(filterStandard));
  }, [subjects, filterStandard]);

  // Header-wise Filters & Sorters for Daily Session Matrix Table
  const dailySessionColumns = useMemo(() => {
    const clusterFilters = Array.from(new Set(dailySessionsList.map(d => d.cluster_name).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b))
      .map(v => ({ text: v, value: v }));

    const standardFilters = Array.from(new Set(dailySessionsList.map(d => d.standard_name).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
      .map(v => ({ text: v, value: v }));

    const subjectFilters = Array.from(new Set(dailySessionsList.map(d => d.subjects_name).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b))
      .map(v => ({ text: v, value: v }));

    const instructorFilters = Array.from(new Set(dailySessionsList.map(d => d.instructor_name).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b))
      .map(v => ({ text: v, value: v }));

    const staffFilters = Array.from(new Set(dailySessionsList.map(d => d.staff_name).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b))
      .map(v => ({ text: v, value: v }));

    const typeFilters = Array.from(new Set(dailySessionsList.map(d => d.session_type).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b))
      .map(v => ({ text: v, value: v }));

    const statusFilters = Array.from(new Set(dailySessionsList.map(d => d.lesson_status).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b))
      .map(v => ({ text: v, value: v }));

    return [
      {
        title: 'Date',
        dataIndex: 'daily_session_date',
        key: 'daily_session_date',
        sorter: (a, b) => dayjs(a.daily_session_date || 0).valueOf() - dayjs(b.daily_session_date || 0).valueOf(),
        defaultSortOrder: 'descend',
        render: d => d ? dayjs(d).format('DD-MM-YYYY') : '—'
      },
      {
        title: 'Start',
        dataIndex: 'start_time',
        key: 'start_time',
        sorter: (a, b) => (a.start_time || '').localeCompare(b.start_time || ''),
        render: val => formatTime12h(val)
      },
      {
        title: 'End',
        dataIndex: 'end_time',
        key: 'end_time',
        sorter: (a, b) => (a.end_time || '').localeCompare(b.end_time || ''),
        render: val => formatTime12h(val)
      },
      {
        title: 'Cluster',
        dataIndex: 'cluster_name',
        key: 'cluster_name',
        filters: clusterFilters,
        onFilter: (value, record) => record.cluster_name === value,
        filterSearch: true,
        sorter: (a, b) => (a.cluster_name || '').localeCompare(b.cluster_name || ''),
        render: val => val || '—'
      },
      {
        title: 'Standard',
        dataIndex: 'standard_name',
        key: 'standard_name',
        filters: standardFilters,
        onFilter: (value, record) => record.standard_name === value,
        filterSearch: true,
        sorter: (a, b) => (a.standard_name || '').localeCompare(b.standard_name || '', undefined, { numeric: true }),
        render: val => val || '—'
      },
      {
        title: 'Subject',
        dataIndex: 'subjects_name',
        key: 'subjects_name',
        filters: subjectFilters,
        onFilter: (value, record) => record.subjects_name === value,
        filterSearch: true,
        sorter: (a, b) => (a.subjects_name || '').localeCompare(b.subjects_name || ''),
        render: val => val || '—'
      },
      {
        title: 'Lesson / Topic',
        dataIndex: 'daily_session_part',
        key: 'daily_session_part',
        sorter: (a, b) => (a.daily_session_part || a.lesson_name || '').localeCompare(b.daily_session_part || b.lesson_name || ''),
        render: (val, r) => formatLessonTopic(val, r.lesson_name)
      },
      {
        title: 'Instructor',
        dataIndex: 'instructor_name',
        key: 'instructor_name',
        filters: instructorFilters,
        onFilter: (value, record) => record.instructor_name === value,
        filterSearch: true,
        sorter: (a, b) => (a.instructor_name || '').localeCompare(b.instructor_name || ''),
        render: val => val || '—'
      },
      {
        title: 'Staff',
        dataIndex: 'staff_name',
        key: 'staff_name',
        filters: staffFilters.length > 0 ? staffFilters : undefined,
        onFilter: (value, record) => record.staff_name === value,
        filterSearch: true,
        sorter: (a, b) => (a.staff_name || '').localeCompare(b.staff_name || ''),
        render: val => val || '—'
      },
      {
        title: 'Type',
        dataIndex: 'session_type',
        key: 'session_type',
        filters: typeFilters,
        onFilter: (value, record) => record.session_type === value,
        sorter: (a, b) => (a.session_type || '').localeCompare(b.session_type || ''),
        render: t => {
          const color = t === 'LIVE' ? 'green' : t === 'RECORDED' ? 'blue' : t === 'CANCELLED' ? 'red' : 'purple';
          return <Tag color={color}>{t || '—'}</Tag>;
        }
      },
      {
        title: 'Count',
        dataIndex: 'session_count',
        key: 'session_count',
        sorter: (a, b) => (Number(a.session_count) || 0) - (Number(b.session_count) || 0),
        render: c => <Tag color="blue">{c !== undefined && c !== null ? c : 1}</Tag>
      },
      {
        title: 'Status',
        dataIndex: 'lesson_status',
        key: 'lesson_status',
        filters: statusFilters,
        onFilter: (value, record) => record.lesson_status === value,
        sorter: (a, b) => (a.lesson_status || '').localeCompare(b.lesson_status || ''),
        render: st => {
          if (!st) return '—';
          const color = st === 'COMPLETED' ? 'success' : st === 'PENDING' ? 'warning' : st === 'CANCELLED' ? 'error' : 'processing';
          return <Tag color={color}>{st}</Tag>;
        }
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 100,
        render: (_, r) => (
          <Space>
            <Button size="small" icon={<EditOutlined />} onClick={() => openModal('daily_session', r)} />
            <Popconfirm title="Delete session?" onConfirm={() => handleDeleteRecord('daily_session', r.daily_session_id)}>
              <Button danger size="small" icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        )
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dailySessionsList]);

  // Form cascading handlers
  const updatePartNumberForForm = async (lessonId, clusterId = formClusterId, ayId = formAcademicYearId, currentEditId = editingRecord?.daily_session_id) => {
    if (!lessonId) {
      if (isClassType) {
        form.setFieldsValue({ daily_session_part: undefined });
      }
      setIsLessonAlreadyCompleted(false);
      setCompletedLessonInfo(null);
      return;
    }
    try {
      const matchingSessions = await fetchDailySessions({
        academic_year_id: ayId || undefined,
        cluster_id: clusterId || undefined,
        lessons_id: lessonId
      });

      const validSessions = Array.isArray(matchingSessions)
        ? matchingSessions.filter(ds => String(ds.daily_session_id) !== String(currentEditId))
        : [];

      // Check if this lesson is already marked as COMPLETED for this cluster and academic year
      const completedSession = validSessions.find(s => String(s.lesson_status || '').toUpperCase() === 'COMPLETED');
      if (completedSession) {
        setIsLessonAlreadyCompleted(true);
        setCompletedLessonInfo(completedSession);
      } else {
        setIsLessonAlreadyCompleted(false);
        setCompletedLessonInfo(null);
      }

      let maxPart = 0;
      let sessionCount = 0;
      for (const s of validSessions) {
        sessionCount += 1;
        const match = String(s.daily_session_part || '').match(/Part[- ]?(\d+)/i);
        if (match) {
          const num = parseInt(match[1], 10);
          if (!isNaN(num) && num > maxPart) {
            maxPart = num;
          }
        }
      }

      const nextPartNum = Math.max(maxPart + 1, sessionCount + 1);
      form.setFieldsValue({
        daily_session_part: `Part-${nextPartNum}`
      });
    } catch (e) {
      console.error('Failed to calculate part number:', e);
      const matchingDs = dailySessionsList.filter(ds => 
        String(ds.lessons_id) === String(lessonId) && 
        String(ds.cluster_id) === String(clusterId) &&
        String(ds.daily_session_id) !== String(currentEditId)
      );
      const completedSession = matchingDs.find(s => String(s.lesson_status || '').toUpperCase() === 'COMPLETED');
      if (completedSession) {
        setIsLessonAlreadyCompleted(true);
        setCompletedLessonInfo(completedSession);
      } else {
        setIsLessonAlreadyCompleted(false);
        setCompletedLessonInfo(null);
      }

      const usedSessions = matchingDs.reduce((acc, ds) => acc + (Number(ds.session_count) || 1), 0);
      form.setFieldsValue({
        daily_session_part: `Part-${usedSessions + 1}`
      });
    }
  };

  const handleFormAcademicYearChange = (ayId) => {
    setFormAcademicYearId(ayId);
    setFormClusterId(null);
    setFormStandardId(null);
    setFormSubjectId(null);
    setIsLessonAlreadyCompleted(false);
    setCompletedLessonInfo(null);
    form.setFieldsValue({ cluster_id: undefined, standard_id: undefined, subjects_id: undefined, lessons_id: undefined, instructor_id: undefined, daily_session_part: undefined });
  };

  const handleFormClusterChange = async (clusterId) => {
    setFormClusterId(clusterId);
    setFormStandardId(null);
    setFormSubjectId(null);
    setIsLessonAlreadyCompleted(false);
    setCompletedLessonInfo(null);
    form.setFieldsValue({ standard_id: undefined, subjects_id: undefined, lessons_id: undefined, instructor_id: undefined, daily_session_part: undefined });

    if (clusterId) {
      try {
        const stds = await fetchStandards({ cluster_id: clusterId, academic_year_id: formAcademicYearId });
        setFormClusterStandards(stds || []);
      } catch (e) {
        setFormClusterStandards([]);
      }
    } else {
      setFormClusterStandards([]);
    }
  };

  const handleFormStandardChange = async (stdId) => {
    setFormStandardId(stdId);
    setFormSubjectId(null);
    setIsLessonAlreadyCompleted(false);
    setCompletedLessonInfo(null);
    form.setFieldsValue({ subjects_id: undefined, lessons_id: undefined, instructor_id: undefined, daily_session_part: undefined });
  };

  const handleFormSubjectChange = (subId) => {
    setFormSubjectId(subId);
    setIsLessonAlreadyCompleted(false);
    setCompletedLessonInfo(null);
    form.setFieldsValue({ lessons_id: undefined, instructor_id: undefined, daily_session_part: undefined });
  };

  const handleFormLessonChange = async (lessonId) => {
    if (lessonId) {
      await updatePartNumberForForm(lessonId, formClusterId, formAcademicYearId, editingRecord?.daily_session_id);
    } else {
      setIsLessonAlreadyCompleted(false);
      setCompletedLessonInfo(null);
      form.setFieldsValue({ daily_session_part: undefined });
    }
  };

  const formAvailableClusters = useMemo(() => {
    if (!formAcademicYearId) return clusters;
    return clusters.filter(c => !c.academic_year_id || String(c.academic_year_id) === String(formAcademicYearId));
  }, [clusters, formAcademicYearId]);

  const formAvailableStandards = useMemo(() => {
    if (modalType === 'daily_session' && formClusterId) {
      return sortStandardsAscending(formClusterStandards);
    }
    if (formAcademicYearId) {
      return sortStandardsAscending(standards.filter(s => !s.academic_year_id || String(s.academic_year_id) === String(formAcademicYearId)));
    }
    return sortStandardsAscending(standards);
  }, [standards, formAcademicYearId, formClusterStandards, modalType, formClusterId]);

  const formAvailableSubjects = useMemo(() => {
    if (!formStandardId) return [];
    return subjects.filter(sub => String(sub.standard_id) === String(formStandardId));
  }, [subjects, formStandardId]);

  const formAvailableLessons = useMemo(() => {
    if (!formSubjectId) return [];
    return sortLessonsAscending(lessons.filter(l => String(l.subjects_id) === String(formSubjectId)));
  }, [lessons, formSubjectId]);

  const activeInstructors = useMemo(() => {
    return instructors.filter(inst => {
      if (!inst.status) return true;
      const st = String(inst.status).trim().toUpperCase();
      return st === 'ACTIVE';
    });
  }, [instructors]);

  const activeStaffList = useMemo(() => {
    return staffList.filter(s => {
      if (!s.status) return true;
      const st = String(s.status).trim().toUpperCase();
      return st === 'ACTIVE';
    });
  }, [staffList]);

  const availableInstructorsForForm = useMemo(() => {
    if (!formStandardId && !formSubjectId) return activeInstructors;
    
    return activeInstructors.filter(inst => {
      if (!inst.subjects_id) return false;
      
      const assignedSubIds = String(inst.subjects_id).split(',').map(s => s.trim());
      
      if (formSubjectId) {
        return assignedSubIds.includes(String(formSubjectId));
      }
      
      if (formStandardId) {
        const stdSubjects = subjects.filter(sub => String(sub.standard_id) === String(formStandardId)).map(s => String(s.subjects_id));
        return assignedSubIds.some(subId => stdSubjects.includes(subId));
      }
      return false;
    });
  }, [activeInstructors, formStandardId, formSubjectId, subjects]);

  const openModal = (type, record = null) => {
    setModalType(type);
    setEditingRecord(record);
    setIsLessonAlreadyCompleted(false);
    setCompletedLessonInfo(null);
    
    if (record) {
      setFormAcademicYearId(record.academic_year_id);
      setFormClusterId(record.cluster_id);
      setFormStandardId(record.standard_id);
      setFormSubjectId(record.subjects_id);

      if (record.cluster_id) {
        fetchStandards({ cluster_id: record.cluster_id, academic_year_id: record.academic_year_id })
          .then(stds => setFormClusterStandards(stds || []))
          .catch(() => setFormClusterStandards([]));
      }

      form.setFieldsValue({
        ...record,
        daily_session_date: record.daily_session_date ? dayjs(record.daily_session_date) : null,
        start_time: record.start_time ? dayjs(record.start_time, 'HH:mm:ss') : null,
        end_time: record.end_time ? dayjs(record.end_time, 'HH:mm:ss') : null
      });
    } else {
      const currentAy = (filterAcademicYear ? academicYears.find(y => y.academic_year_id === filterAcademicYear) : null)
        || getCurrentAcademicYear(academicYears);
      const defaultAyId = currentAy ? currentAy.academic_year_id : null;
      setFormAcademicYearId(defaultAyId);
      setFormClusterId(filterCluster || null);
      setFormStandardId(null);
      setFormSubjectId(null);

      if (filterCluster && defaultAyId) {
        fetchStandards({ cluster_id: filterCluster, academic_year_id: defaultAyId })
          .then(stds => setFormClusterStandards(stds || []))
          .catch(() => setFormClusterStandards([]));
      } else {
        setFormClusterStandards([]);
      }
      
      form.resetFields();
      form.setFieldsValue({
        academic_year_id: defaultAyId,
        cluster_id: filterCluster || undefined,
        session_type: 'LIVE',
        session_count: 1,
        daily_session_date: dayjs()
      });
    }
  };

  const handleFormSubmit = async (values) => {
    if (isClassType && isLessonAlreadyCompleted) {
      message.error('This Lesson / Topic is already marked as COMPLETED for this cluster and academic year.');
      return;
    }
    setModalLoading(true);
    try {
      const payload = { ...values };
      if (values.daily_session_date) {
        payload.daily_session_date = values.daily_session_date.format('YYYY-MM-DD');
      }
      if (values.start_time) {
        payload.start_time = values.start_time.format('HH:mm:ss');
      }
      if (values.end_time) {
        payload.end_time = values.end_time.format('HH:mm:ss');
      }

      if (editingRecord) {
        await updateDailySession(editingRecord.daily_session_id, payload);
        message.success('Daily session record updated successfully');
      } else {
        await createDailySession(payload);
        message.success('Daily session record created successfully');
      }
      
      setModalType(null);
      loadDailySessions();
    } catch (err) {
      console.error('Failed to submit form:', err);
      message.error('Failed to save session record.');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteRecord = async (type, id) => {
    try {
      if (type === 'daily_session') {
        await deleteDailySession(id);
        message.success('Session record deleted successfully');
        loadDailySessions();
      }
    } catch (err) {
      message.error('Failed to delete record.');
    }
  };

  const downloadCSV = (type) => {
    if (type === 'daily_session') {
      const selectedClusterName = filterCluster ? clusters.find(c => String(c.cluster_id) === String(filterCluster))?.cluster_name || 'All Clusters' : 'All Clusters';
      const selectedAyName = filterAcademicYear ? academicYears.find(ay => String(ay.academic_year_id) === String(filterAcademicYear))?.academic_year || 'All Academic Years' : 'All Academic Years';
      const selectedStdName = filterStandard ? standards.find(s => String(s.standard_id) === String(filterStandard))?.standard_name || 'All Standards' : 'All Standards';
      const selectedMonth = filterDate ? filterDate.format('MMMM YYYY') : 'All Months';

      const csvRows = [];
      
      // Context/Filters Section
      csvRows.push('"=== FILTER CONTEXT ==="');
      csvRows.push(`"Academic Year: ${selectedAyName}"`);
      csvRows.push(`"Cluster: ${selectedClusterName}"`);
      csvRows.push(`"Standard: ${selectedStdName}"`);
      csvRows.push(`"Month: ${selectedMonth}"`);
      csvRows.push('');
      
      // 1. Instructor Session Matrix Section
      csvRows.push('"=== INSTRUCTOR SESSION MATRIX SUMMARY ==="');
      
      matrixGroups.forEach(group => {
        const teacherMatrixData = group.data;
        csvRows.push(`"--- Cluster: ${group.clusterName} | Academic Year: ${selectedAyName} | Month: ${selectedMonth} ---"`);
        const matrixHeaders = ['TEACHER', ...teacherMatrixData.sortedStds.map(s => `"${s.standard_name}"`)].join(',');
        csvRows.push(matrixHeaders);

        teacherMatrixData.teacherRows.forEach(t => {
          const row = [
            `"${t.instructor_name || ''}"`,
            ...teacherMatrixData.sortedStds.map(std => {
              const live = t.liveCounts[std.standard_id] || 0;
              const rec = t.recordedCounts[std.standard_id] || 0;
              if (live > 0 && rec > 0) return `"${live} Live + ${rec} Rec"`;
              if (rec > 0) return `"${rec} Rec"`;
              return live || 0;
            })
          ];
          csvRows.push(row.join(','));
        });

        const liveSubtotalRow = [
          '"SUBTOTAL (LIVE CLASS)"',
          ...teacherMatrixData.sortedStds.map(std => teacherMatrixData.stdLiveSubtotals[std.standard_id] || 0)
        ];
        csvRows.push(liveSubtotalRow.join(','));

        const recSubtotalRow = [
          '"SUBTOTAL (RECORDED CLASS)"',
          ...teacherMatrixData.sortedStds.map(std => teacherMatrixData.stdRecordedSubtotals[std.standard_id] || 0)
        ];
        csvRows.push(recSubtotalRow.join(','));

        const subtotalRow = [
          '"SUBTOTAL (TOTAL CONDUCTED)"',
          ...teacherMatrixData.sortedStds.map(std => teacherMatrixData.stdSubtotals[std.standard_id] || 0)
        ];
        csvRows.push(subtotalRow.join(','));

        csvRows.push(`"TOTAL LIVE CLASS",${teacherMatrixData.totalLiveCount}`);
        csvRows.push(`"TOTAL RECORDED CLASS",${teacherMatrixData.totalRecordedCount}`);
        csvRows.push(`"TOTAL SESSIONS CONDUCTED",${teacherMatrixData.totalClassCount}`);
        csvRows.push(`"TOTAL ALLOCATED SESSIONS (ACADEMIC YEAR)",${teacherMatrixData.totalAllocatedSessions}`);
        csvRows.push(`"TOTAL REMAINING SESSIONS (ACADEMIC YEAR)",${teacherMatrixData.remainingClass}`);
        csvRows.push(`"CLASS CANCELLED",${teacherMatrixData.totalCancelledCount}`);
        csvRows.push('');
      });

      csvRows.push('');
      csvRows.push('"=== DETAILED DAILY SESSION RECORDS ==="');

      // 2. Detailed Daily Session Records
      const recordHeaders = ['Date', 'Start Time', 'End Time', 'Cluster', 'Standard', 'Subject', 'Lesson / Topic', 'Instructor', 'Staff', 'Type', 'Count', 'Status'];
      csvRows.push(recordHeaders.map(h => `"${h}"`).join(','));

      dailySessionsList.forEach(ds => {
        const row = [
          `"${ds.daily_session_date ? dayjs(ds.daily_session_date).format('DD-MM-YYYY') : ''}"`,
          `"${formatTime12h(ds.start_time)}"`,
          `"${formatTime12h(ds.end_time)}"`,
          `"${ds.cluster_name || ''}"`,
          `"${ds.standard_name || ''}"`,
          `"${ds.subjects_name || ''}"`,
          `"${formatLessonTopic(ds.daily_session_part, ds.lesson_name, '').replace(/"/g, '""')}"`,
          `"${ds.instructor_name || ''}"`,
          `"${ds.staff_name || ''}"`,
          `"${ds.session_type || ''}"`,
          `"${ds.session_count || 1}"`,
          `"${ds.lesson_status || ''}"`
        ];
        csvRows.push(row.join(','));
      });

      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.setAttribute('href', url);
      a.setAttribute('download', `session_control_matrix_${dayjs().format('YYYY-MM-DD')}.csv`);
      a.click();
    }
  };

  const downloadCSVTemplate = () => {
    const headers = [
      'Session Type',
      'Session Date',
      'Academic Year',
      'Cluster',
      'Standard',
      'Subject',
      'Instructor',
      'Staff',
      'Lesson',
      'Topic Details / Part',
      'Start Time',
      'End Time',
      'Lesson Status',
      'Session Count'
    ];

    const currentAyName = academicYears[0]?.academic_year || '2026-2027';
    const currentClusName = clusters[0]?.cluster_name || 'Cluster 1';
    const currentStdName = standards[0]?.standard_name || 'Standard 10';
    const currentSubName = subjects[0]?.subjects_name || 'Mathematics';
    const currentInstName = instructors[0]?.instructor_name || 'John Doe';
    const currentStaffName = staffList[0]?.staff_name || 'Staff Member';
    const currentLessonName = lessons[0]?.lesson_name || 'Chapter 1 - Real Numbers';

    const sampleRow1 = [
      'LIVE',
      dayjs().format('YYYY-MM-DD'),
      currentAyName,
      currentClusName,
      currentStdName,
      currentSubName,
      currentInstName,
      currentStaffName,
      currentLessonName,
      'Part-1 Introduction & Concepts',
      '09:30 AM',
      '10:30 AM',
      'COMPLETED',
      '1'
    ];

    const sampleRow2 = [
      'RECORDED',
      dayjs().format('YYYY-MM-DD'),
      currentAyName,
      currentClusName,
      standards[1]?.standard_name || currentStdName,
      subjects[1]?.subjects_name || 'Science',
      instructors[1]?.instructor_name || currentInstName,
      currentStaffName,
      lessons[1]?.lesson_name || 'Chapter 2 - Chemical Reactions',
      'Part-2 Balancing Equations',
      '11:00 AM',
      '12:00 PM',
      'PENDING',
      '1'
    ];

    const sampleRow3 = [
      'CANCELLED',
      dayjs().add(1, 'day').format('YYYY-MM-DD'),
      currentAyName,
      clusters[1]?.cluster_name || currentClusName,
      standards[0]?.standard_name || currentStdName,
      currentSubName,
      currentInstName,
      currentStaffName,
      '',
      'School Holiday / Sports Event',
      '02:00 PM',
      '03:00 PM',
      'CANCELLED',
      '0'
    ];

    const content = [
      headers.join(','),
      sampleRow1.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','),
      sampleRow2.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','),
      sampleRow3.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')
    ].join('\n');

    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'Daily_Session_Create_Template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadPDF = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      message.error('Please allow popups to Export PDF / Print');
      return;
    }

    const selectedClusterName = filterCluster ? clusters.find(c => String(c.cluster_id) === String(filterCluster))?.cluster_name || 'All Clusters' : 'All Clusters';
    const selectedAyName = filterAcademicYear ? academicYears.find(ay => String(ay.academic_year_id) === String(filterAcademicYear))?.academic_year || 'All Academic Years' : 'All Academic Years';
    const selectedStdName = filterStandard ? standards.find(s => String(s.standard_id) === String(filterStandard))?.standard_name || 'All Standards' : 'All Standards';
    const selectedMonth = filterDate ? filterDate.format('MMMM YYYY') : 'All Months';

    const matricesHtml = matrixGroups.map(group => {
      const teacherMatrixData = group.data;
      const stdHeadersHtml = teacherMatrixData.sortedStds.map(s => `<th>${s.standard_name.toUpperCase()}</th>`).join('');
      
      const teacherRowsHtml = teacherMatrixData.teacherRows.map(t => {
        const stdCells = teacherMatrixData.sortedStds.map(std => {
          const live = t.liveCounts[std.standard_id] || 0;
          const rec = t.recordedCounts[std.standard_id] || 0;
          if (live > 0 && rec > 0) return `<td><span class="live-tag">${live} Live</span> <span class="rec-tag">${rec} Rec</span></td>`;
          if (rec > 0) return `<td><span class="rec-tag">${rec} Rec</span></td>`;
          if (live > 0) return `<td><span class="live-tag">${live}</span></td>`;
          return `<td>0</td>`;
        }).join('');
        return `<tr><td style="text-align: left; font-weight: 600;">${t.instructor_name}</td>${stdCells}</tr>`;
      }).join('');

      const liveSubtotalCells = teacherMatrixData.sortedStds.map(std => `<td><strong>${teacherMatrixData.stdLiveSubtotals[std.standard_id] || 0}</strong></td>`).join('');
      const recSubtotalCells = teacherMatrixData.sortedStds.map(std => `<td><strong>${teacherMatrixData.stdRecordedSubtotals[std.standard_id] || 0}</strong></td>`).join('');
      const subtotalCells = teacherMatrixData.sortedStds.map(std => `<td><strong>${teacherMatrixData.stdSubtotals[std.standard_id] || 0}</strong></td>`).join('');

      return `
        <h3>Instructor Session Matrix Summary - ${group.clusterName} | ${selectedAyName} | ${selectedMonth}</h3>
        <table>
          <thead>
            <tr>
              <th style="text-align: left;">TEACHER</th>
              ${stdHeadersHtml}
            </tr>
          </thead>
          <tbody>
            ${teacherRowsHtml}
            <tr class="live-subtotal-row">
              <td style="text-align: left;">SUBTOTAL (LIVE CLASS)</td>
              ${liveSubtotalCells}
            </tr>
            <tr class="rec-subtotal-row">
              <td style="text-align: left;">SUBTOTAL (RECORDED CLASS)</td>
              ${recSubtotalCells}
            </tr>
            <tr class="summary-row">
              <td style="text-align: left;">SUBTOTAL (TOTAL CONDUCTED)</td>
              ${subtotalCells}
            </tr>
            <tr class="live-total-row">
              <td style="text-align: left;">TOTAL LIVE CLASS</td>
              <td colSpan="${teacherMatrixData.sortedStds.length}">${teacherMatrixData.totalLiveCount} Sessions Conducted</td>
            </tr>
            <tr class="remaining-row">
              <td style="text-align: left;">TOTAL REMAINING SESSIONS (ACADEMIC YEAR)</td>
              <td colSpan="${teacherMatrixData.sortedStds.length}">${teacherMatrixData.remainingClass} Sessions Remaining (out of ${teacherMatrixData.totalAllocatedSessions} Allocated for Academic Year)</td>
            </tr>
            <tr class="rec-total-row">
              <td style="text-align: left;">TOTAL RECORDED CLASS</td>
              <td colSpan="${teacherMatrixData.sortedStds.length}">${teacherMatrixData.totalRecordedCount} Sessions Conducted</td>
            </tr>
            <tr class="total-row">
              <td style="text-align: left;">TOTAL SESSIONS CONDUCTED</td>
              <td colSpan="${teacherMatrixData.sortedStds.length}">${teacherMatrixData.totalClassCount} Total Sessions (${teacherMatrixData.totalLiveCount} Live + ${teacherMatrixData.totalRecordedCount} Recorded)</td>
            </tr>
            <tr class="cancelled-row">
              <td style="text-align: left;">CLASS CANCELLED</td>
              <td colSpan="${teacherMatrixData.sortedStds.length}">${teacherMatrixData.totalCancelledCount} Sessions Cancelled</td>
            </tr>
          </tbody>
        </table>
      `;
    }).join('');

    const sessionRowsHtml = dailySessionsList.map(ds => `
      <tr>
        <td>${ds.daily_session_date ? dayjs(ds.daily_session_date).format('DD-MM-YYYY') : '—'}</td>
        <td>${formatTime12h(ds.start_time)}</td>
        <td>${formatTime12h(ds.end_time)}</td>
        <td>${ds.cluster_name || '—'}</td>
        <td>${ds.standard_name || '—'}</td>
        <td>${ds.subjects_name || '—'}</td>
        <td style="text-align: left;">${formatLessonTopic(ds.daily_session_part, ds.lesson_name)}</td>
        <td>${ds.instructor_name || '—'}</td>
        <td>${ds.staff_name || '—'}</td>
        <td>${ds.session_type || 'LIVE'}</td>
        <td>${ds.session_count || 1}</td>
        <td>${ds.lesson_status || '—'}</td>
      </tr>
    `).join('');

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Session Control Matrix Report - ${dayjs().format('DD-MM-YYYY')}</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 20px; color: #111; }
            h2 { color: #1890ff; border-bottom: 2px solid #1890ff; padding-bottom: 8px; margin-bottom: 5px; }
            h3 { color: #333; margin-top: 25px; margin-bottom: 10px; font-size: 15px; font-weight: bold; }
            .meta-info { font-size: 12px; color: #666; margin-bottom: 20px; line-height: 1.5; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 12px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: center; }
            th { background-color: #f0f2f5; font-weight: bold; }
            .live-tag { background-color: #e6fffb; color: #08979c; border: 1px solid #87e8de; padding: 2px 6px; border-radius: 4px; font-size: 11px; font-weight: bold; }
            .rec-tag { background-color: #e6f7ff; color: #096dd9; border: 1px solid #91d5ff; padding: 2px 6px; border-radius: 4px; font-size: 11px; font-weight: bold; }
            .live-subtotal-row { background-color: #e6fffb; font-weight: bold; color: #08979c; }
            .rec-subtotal-row { background-color: #e6f7ff; font-weight: bold; color: #096dd9; }
            .summary-row { background-color: #fafafa; font-weight: bold; }
            .live-total-row { background-color: #e6fffb; font-weight: bold; color: #08979c; }
            .rec-total-row { background-color: #e6f7ff; font-weight: bold; color: #096dd9; }
            .total-row { background-color: #f0f5ff; font-weight: bold; color: #2f54eb; }
            .remaining-row { background-color: #f9f0ff; font-weight: bold; color: #722ed1; }
            .cancelled-row { background-color: #fff1f0; font-weight: bold; color: #ff4d4f; }
            @media print {
              body { padding: 0; }
              @page { size: A4 landscape; margin: 15mm; }
            }
          </style>
        </head>
        <body>
          <h2>VEDIKA - Session Control Management Report</h2>
          <div class="meta-info">
            Generated on: <strong>${dayjs().format('DD-MM-YYYY HH:mm:ss')}</strong> | Total Session Records: <strong>${dailySessionsList.length}</strong><br/>
            Filters Applied: <strong>Academic Year:</strong> ${selectedAyName} | <strong>Cluster:</strong> ${selectedClusterName} | <strong>Standard:</strong> ${selectedStdName} | <strong>Month:</strong> ${selectedMonth}
          </div>

          ${matricesHtml}

          <h3>Daily Session Detailed Records</h3>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Start Time</th>
                <th>End Time</th>
                <th>Cluster</th>
                <th>Standard</th>
                <th>Subject</th>
                <th style="text-align: left;">Lesson / Topic</th>
                <th>Instructor</th>
                <th>Staff</th>
                <th>Type</th>
                <th>Count</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${sessionRowsHtml}
            </tbody>
          </table>

          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
              }, 400);
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const normalizeTimeStr = (t) => {
    if (!t || typeof t !== 'string') return null;
    const trimmed = t.trim();
    if (!trimmed) return null;
    if (/^\d{1,2}:\d{2}:\d{2}$/.test(trimmed)) {
      const [h, m, s] = trimmed.split(':');
      return `${h.padStart(2, '0')}:${m.padStart(2, '0')}:${s.padStart(2, '0')}`;
    }
    if (/^\d{1,2}:\d{2}$/.test(trimmed)) {
      const [h, m] = trimmed.split(':');
      return `${h.padStart(2, '0')}:${m.padStart(2, '0')}:00`;
    }
    const match12 = trimmed.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i);
    if (match12) {
      let h = parseInt(match12[1], 10);
      const m = match12[2].padStart(2, '0');
      const s = match12[3] ? match12[3].padStart(2, '0') : '00';
      const ampm = match12[4].toUpperCase();
      if (ampm === 'PM' && h < 12) h += 12;
      if (ampm === 'AM' && h === 12) h = 0;
      return `${String(h).padStart(2, '0')}:${m}:${s}`;
    }
    return trimmed;
  };

  // --- CSV Upload Handling with Preview ---
  const handleCsvFileUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const parsed = parseCSVText(text);

      if (parsed.length < 2) {
        message.error('CSV file must contain a header row and at least one data row.');
        return;
      }

      // Column identification helper
      const identifyColumn = (rawH) => {
        if (!rawH) return null;
        const h = rawH.toUpperCase().replace(/[^A-Z0-9]/g, '');
        if (h === 'SESSIONSTATUS' || h === 'LESSONSTATUS' || h === 'STATUS') return 'status';
        if (h === 'SESSIONCOUNT' || h === 'COUNT') return 'count';
        if (h === 'STARTTIME' || h === 'START') return 'start_time';
        if (h === 'ENDTIME' || h === 'END') return 'end_time';
        if (h === 'SESSIONTYPE' || h === 'TYPE') return 'session_type';
        if (h === 'SESSIONDATE' || h === 'DATE') return 'date';
        if (h.includes('ACADEMIC') || h === 'AY' || h === 'YEAR') return 'academic_year';
        if (h.includes('CLUSTER')) return 'cluster';
        if (h.includes('STANDARD') || h.includes('GRADE') || h === 'STD' || h === 'CLASS') return 'standard';
        if (h.includes('SUBJECT') || h === 'SUB') return 'subject';
        if (h.includes('STAFF')) return 'staff';
        if (h.includes('INSTRUCTOR') || h.includes('TEACHER') || h.includes('FACULTY')) return 'instructor';
        if (h === 'TOPICDETAILSPART' || h === 'TOPICDETAILS' || h === 'TOPIC' || h === 'PART' || h === 'NOTE' || h === 'REASON') return 'part';
        if (h === 'LESSON' || h === 'CHAPTER' || h === 'LESSONNAME' || h === 'CHAPTERNAME') return 'lesson';
        if (h.includes('LESSON') && h.includes('TOPIC')) return 'lesson_topic';
        if (h.includes('LESSON') || h.includes('CHAPTER')) return 'lesson';
        if (h.includes('TOPIC') || h.includes('PART') || h.includes('NOTE') || h.includes('REASON')) return 'part';
        return null;
      };

      // Auto-find actual column header row (skipping title/filter context lines if any)
      let headerRowIndex = 0;
      for (let i = 0; i < Math.min(parsed.length, 15); i++) {
        const row = parsed[i];
        const identifiedCount = row.filter(cell => identifyColumn(cell) !== null).length;
        if (identifiedCount >= 3) {
          headerRowIndex = i;
          break;
        }
      }

      const headers = parsed[headerRowIndex];
      const colMap = {};
      headers.forEach((h, idx) => {
        const colKey = identifyColumn(h);
        if (colKey) colMap[colKey] = idx;
      });

      const rows = parsed.slice(headerRowIndex + 1);
      const parsedSessions = [];

      rows.forEach(rowArr => {
        if (rowArr.length === 0 || !rowArr.some(cell => cell && cell.trim() !== '')) return;

        const getVal = (key) => {
          const idx = colMap[key];
          return idx !== undefined && rowArr[idx] !== undefined ? rowArr[idx].trim() : '';
        };

        const rawType = getVal('session_type');
        const rawDate = getVal('date');
        const rawAy = getVal('academic_year');
        const rawCluster = getVal('cluster');
        const rawStd = getVal('standard');
        const rawSub = getVal('subject');
        const rawInst = getVal('instructor');
        const rawStaff = getVal('staff');
        const rawLesson = getVal('lesson') || getVal('lesson_topic');
        const rawPart = getVal('part') || (getVal('lesson') ? '' : getVal('lesson_topic'));
        const rawStart = getVal('start_time');
        const rawEnd = getVal('end_time');
        const rawStatus = getVal('status');
        const rawCount = getVal('count');

        // Resolve Date
        let dateVal = rawDate;
        if (dateVal) {
          if (/^\d{2}-\d{2}-\d{4}$/.test(dateVal)) {
            const [d, m, y] = dateVal.split('-');
            dateVal = `${y}-${m}-${d}`;
          } else if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateVal)) {
            const [d, m, y] = dateVal.split('/');
            dateVal = `${y}-${m}-${d}`;
          }
        }
        if (!dateVal) {
          dateVal = filterDate ? dayjs(filterDate).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
        }

        // Resolve Academic Year
        let matchedAy = academicYears.find(a => {
          if (String(a.academic_year_id) === String(rawAy)) return true;
          if (!a.academic_year) return false;
          const aName = a.academic_year.trim().toLowerCase();
          const rName = rawAy.trim().toLowerCase();
          return aName === rName || aName.replace(/\s+/g, '') === rName.replace(/\s+/g, '');
        });
        if (!matchedAy && filterAcademicYear) {
          matchedAy = academicYears.find(a => String(a.academic_year_id) === String(filterAcademicYear));
        }
        if (!matchedAy && academicYears.length > 0) {
          matchedAy = academicYears[0];
        }
        const ayId = matchedAy ? matchedAy.academic_year_id : (rawAy && !isNaN(rawAy) ? parseInt(rawAy) : null);
        const ayName = matchedAy ? matchedAy.academic_year : (rawAy || '—');

        // Resolve Cluster
        let matchedCluster = clusters.find(c => {
          if (String(c.cluster_id) === String(rawCluster)) return true;
          if (!c.cluster_name) return false;
          const cName = c.cluster_name.trim().toLowerCase();
          const rName = rawCluster.trim().toLowerCase();
          return cName === rName || cName.includes(rName) || rName.includes(cName);
        });
        if (!matchedCluster && filterCluster) {
          matchedCluster = clusters.find(c => String(c.cluster_id) === String(filterCluster));
        }
        const clusterId = matchedCluster ? matchedCluster.cluster_id : (rawCluster && !isNaN(rawCluster) ? parseInt(rawCluster) : null);
        const clusterName = matchedCluster ? matchedCluster.cluster_name : (rawCluster || '—');

        // Resolve Standard
        let matchedStd = standards.find(s => {
          if (String(s.standard_id) === String(rawStd)) return true;
          if (!s.standard_name) return false;
          const sName = s.standard_name.trim().toLowerCase();
          const rName = rawStd.trim().toLowerCase();
          if (sName === rName) return true;
          const sClean = sName.replace(/standard|class|grade|th|st|nd|rd/g, '').trim();
          const rClean = rName.replace(/standard|class|grade|th|st|nd|rd/g, '').trim();
          return sClean && rClean && sClean === rClean;
        });
        if (!matchedStd && filterStandard) {
          matchedStd = standards.find(s => String(s.standard_id) === String(filterStandard));
        }
        const standardId = matchedStd ? matchedStd.standard_id : (rawStd && !isNaN(rawStd) ? parseInt(rawStd) : null);
        const standardName = matchedStd ? matchedStd.standard_name : (rawStd || '—');

        // Resolve Subject
        let matchedSub = subjects.find(s => {
          if (String(s.subjects_id) === String(rawSub)) return true;
          if (!s.subjects_name) return false;
          const sName = s.subjects_name.trim().toLowerCase();
          const rName = rawSub.trim().toLowerCase();
          return sName === rName || sName.includes(rName) || rName.includes(sName);
        });
        if (!matchedSub && filterSubject) {
          matchedSub = subjects.find(s => String(s.subjects_id) === String(filterSubject));
        }
        const subId = matchedSub ? matchedSub.subjects_id : (rawSub && !isNaN(rawSub) ? parseInt(rawSub) : null);
        const subName = matchedSub ? matchedSub.subjects_name : (rawSub || '—');

        // Resolve Instructor
        let matchedInst = instructors.find(i => {
          if (String(i.instructor_id) === String(rawInst)) return true;
          if (!i.instructor_name) return false;
          const iName = i.instructor_name.trim().toLowerCase();
          const rName = rawInst.trim().toLowerCase();
          return iName === rName || iName.includes(rName) || rName.includes(iName);
        });
        if (!matchedInst && filterInstructor) {
          matchedInst = instructors.find(i => String(i.instructor_id) === String(filterInstructor));
        }
        const instId = matchedInst ? matchedInst.instructor_id : (rawInst && !isNaN(rawInst) ? parseInt(rawInst) : null);
        const instName = matchedInst ? matchedInst.instructor_name : (rawInst || '—');

        // Resolve Staff
        let matchedStaff = staffList.find(s => {
          if (String(s.staff_id) === String(rawStaff)) return true;
          if (!s.staff_name) return false;
          const sName = s.staff_name.trim().toLowerCase();
          const rName = rawStaff.trim().toLowerCase();
          return sName === rName || sName.includes(rName) || rName.includes(sName);
        });
        const staffId = matchedStaff ? matchedStaff.staff_id : (rawStaff && !isNaN(rawStaff) ? parseInt(rawStaff) : null);
        const staffName = matchedStaff ? matchedStaff.staff_name : (rawStaff || '—');

        // Resolve Lesson & Part
        let matchedLesson = lessons.find(l => {
          if (String(l.lessons_id) === String(rawLesson)) return true;
          if (!l.lesson_name) return false;
          const lName = l.lesson_name.trim().toLowerCase();
          const rName = rawLesson.trim().toLowerCase();
          return lName === rName || lName.includes(rName) || rName.includes(lName);
        });
        const lessonId = matchedLesson ? matchedLesson.lessons_id : (rawLesson && !isNaN(rawLesson) ? parseInt(rawLesson) : null);
        const lessonName = matchedLesson ? matchedLesson.lesson_name : (rawLesson || '—');
        const topicPart = rawPart || (rawLesson && !matchedLesson ? rawLesson : (matchedLesson ? matchedLesson.lesson_name : '—'));

        const sessionType = (rawType || 'LIVE').toUpperCase();
        const sessionCount = rawCount !== '' ? parseFloat(rawCount) : (sessionType === 'CANCELLED' ? 0 : 1);
        const lessonStatus = (rawStatus || (sessionType === 'CANCELLED' ? 'CANCELLED' : 'COMPLETED')).toUpperCase();

        parsedSessions.push({
          daily_session_date: dateVal,
          academic_year_id: ayId,
          academic_year_name: ayName,
          cluster_id: clusterId,
          cluster_name: clusterName,
          standard_id: standardId,
          standard_name: standardName,
          subjects_id: subId,
          subjects_name: subName,
          lessons_id: lessonId,
          lesson_name: lessonName,
          daily_session_part: topicPart,
          instructor_id: instId,
          instructor_name: instName,
          staff_id: staffId,
          staff_name: staffName,
          start_time: normalizeTimeStr(rawStart),
          end_time: normalizeTimeStr(rawEnd),
          session_type: sessionType,
          session_count: isNaN(sessionCount) ? 1 : sessionCount,
          lesson_status: lessonStatus
        });
      });

      if (parsedSessions.length === 0) {
        message.error('No valid daily session records found in CSV file.');
        return;
      }

      setCsvParsedData(parsedSessions);
      setIsCsvModalOpen(true);
    };

    reader.readAsText(file);
    return false;
  };

  const handleConfirmCsvUpload = async () => {
    setUploadingCsv(true);
    try {
      await bulkUploadDailySessions({ sessions: csvParsedData, rows: csvParsedData });
      message.success(`Successfully imported ${csvParsedData.length} daily sessions!`);
      setIsCsvModalOpen(false);
      setCsvParsedData([]);
      loadDailySessions();
    } catch (err) {
      message.error(err.message || 'Failed to import daily sessions.');
    } finally {
      setUploadingCsv(false);
    }
  };

  const handleAcademicYearFilterChange = (val) => {
    setFilterAcademicYear(val);
    setFilterCluster(null);
    setFilterStandard(null);
    setFilterSubject(null);
    setClusterFilteredStandards([]);
  };

  const handleClusterFilterChange = async (val) => {
    setFilterCluster(val);
    setFilterStandard(null);
    setFilterSubject(null);
    if (val) {
      try {
        const stds = await fetchStandards({ cluster_id: val, academic_year_id: filterAcademicYear });
        if (stds && Array.isArray(stds)) {
          setClusterFilteredStandards(stds);
        }
      } catch (e) {
        setClusterFilteredStandards([]);
      }
    } else {
      setClusterFilteredStandards([]);
    }
  };

  const sessionTypeValue = Form.useWatch('session_type', form);
  const reasonNoteValue = Form.useWatch('daily_session_part', form);
  const isClassType = sessionTypeValue !== 'CANCELLED' && sessionTypeValue !== 'EXAM';

  const rNoteStr = (reasonNoteValue || '').toString().toLowerCase();
  const isCreditedCancellationReason = rNoteStr.includes('technical') || rNoteStr.includes('rain');
  const isInstructorStaffDisabled = sessionTypeValue === 'CANCELLED' && !isCreditedCancellationReason;

  useEffect(() => {
    if (modalType === 'daily_session' && sessionTypeValue === 'CANCELLED' && !isCreditedCancellationReason) {
      form.setFieldsValue({
        instructor_id: null,
        staff_id: null
      });
    }
    if (modalType === 'daily_session' && isClassType) {
      const currentLesson = form.getFieldValue('lessons_id');
      const currentPart = form.getFieldValue('daily_session_part');
      if (currentLesson && (!currentPart || !/^Part-\d+$/i.test(String(currentPart).trim()))) {
        updatePartNumberForForm(currentLesson, formClusterId, formAcademicYearId, editingRecord?.daily_session_id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionTypeValue, isCreditedCancellationReason, isClassType, modalType, form, formClusterId, formAcademicYearId, editingRecord]);

  // Filter bar for Instructor Session Matrix (no Subject filter — matrix shows standards as columns)
  const matrixFilters = (
    <Card style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-color)', borderRadius: 12, marginBottom: 16 }} bodyStyle={{ padding: 16 }}>
      <Row gutter={[16, 16]} align="middle">
        <Col xs={24} sm={12} md={5}>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Academic Year</span>
          <Select {...selectSearchProps}
            style={{ width: '100%' }}
            value={filterAcademicYear}
            onChange={handleAcademicYearFilterChange}
            allowClear
            placeholder="All Academic Years"
          >
            {academicYears.map(ay => (
              <Option key={ay.academic_year_id} value={ay.academic_year_id}>{ay.academic_year}</Option>
            ))}
          </Select>
        </Col>

        <Col xs={24} sm={12} md={5}>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Cluster</span>
          <Select {...selectSearchProps}
            style={{ width: '100%' }}
            value={filterCluster}
            onChange={handleClusterFilterChange}
            allowClear={!isStudio}
            placeholder="Select Cluster"
          >
            {availableClusters.map(c => (
              <Option key={c.cluster_id} value={c.cluster_id}>
                {c.cluster_name || `Cluster #${c.cluster_id}`} {c.cluster_code ? `(${c.cluster_code})` : ''}
              </Option>
            ))}
          </Select>
        </Col>

        <Col xs={24} sm={12} md={5}>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Standard</span>
          <Select {...selectSearchProps}
            style={{ width: '100%' }}
            value={filterStandard}
            onChange={(val) => { setFilterStandard(val); setFilterSubject(null); }}
            allowClear
            placeholder="All Standards"
          >
            {availableStandardsForTab1.map(s => (
              <Option key={s.standard_id} value={s.standard_id}>{s.standard_name} ({s.standard_medium})</Option>
            ))}
          </Select>
        </Col>

        <Col xs={24} sm={12} md={5}>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Instructor</span>
          <Select {...selectSearchProps}
            style={{ width: '100%' }}
            value={filterInstructor}
            onChange={setFilterInstructor}
            allowClear
            placeholder="All Instructors"
          >
            {activeInstructors.map(inst => (
              <Option key={inst.instructor_id} value={inst.instructor_id}>{inst.instructor_name}</Option>
            ))}
          </Select>
        </Col>

        <Col xs={24} sm={12} md={4}>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Month / Date</span>
          <DatePicker 
            style={{ width: '100%' }}
            picker="month"
            value={filterDate}
            onChange={setFilterDate}
            format="MMMM YYYY"
          />
        </Col>
      </Row>
    </Card>
  );

  // Filter bar for Session Control Management (includes Subject filter — now fixed to hit API)
  const sessionFilters = (
    <Card style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-color)', borderRadius: 12, marginBottom: 16 }} bodyStyle={{ padding: 16 }}>
      <Row gutter={[16, 16]} align="middle">
        <Col xs={24} sm={12} md={4}>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Academic Year</span>
          <Select {...selectSearchProps}
            style={{ width: '100%' }}
            value={filterAcademicYear}
            onChange={handleAcademicYearFilterChange}
            allowClear
            placeholder="All Academic Years"
          >
            {academicYears.map(ay => (
              <Option key={ay.academic_year_id} value={ay.academic_year_id}>{ay.academic_year}</Option>
            ))}
          </Select>
        </Col>

        <Col xs={24} sm={12} md={4}>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Cluster</span>
          <Select {...selectSearchProps}
            style={{ width: '100%' }}
            value={filterCluster}
            onChange={handleClusterFilterChange}
            allowClear={!isStudio}
            placeholder="Select Cluster"
          >
            {availableClusters.map(c => (
              <Option key={c.cluster_id} value={c.cluster_id}>
                {c.cluster_name || `Cluster #${c.cluster_id}`} {c.cluster_code ? `(${c.cluster_code})` : ''}
              </Option>
            ))}
          </Select>
        </Col>

        <Col xs={24} sm={12} md={4}>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Standard</span>
          <Select {...selectSearchProps}
            style={{ width: '100%' }}
            value={filterStandard}
            onChange={(val) => { setFilterStandard(val); setFilterSubject(null); }}
            allowClear
            placeholder="All Standards"
          >
            {availableStandardsForTab1.map(s => (
              <Option key={s.standard_id} value={s.standard_id}>{s.standard_name} ({s.standard_medium})</Option>
            ))}
          </Select>
        </Col>

        <Col xs={24} sm={12} md={4}>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Subject</span>
          <Select {...selectSearchProps}
            style={{ width: '100%' }}
            value={filterSubject}
            onChange={setFilterSubject}
            allowClear
            placeholder="All Subjects"
          >
            {availableSubjectsForTab1.map(sub => (
              <Option key={sub.subjects_id} value={sub.subjects_id}>{sub.subjects_name}</Option>
            ))}
          </Select>
        </Col>

        <Col xs={24} sm={12} md={4}>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Instructor</span>
          <Select {...selectSearchProps}
            style={{ width: '100%' }}
            value={filterInstructor}
            onChange={setFilterInstructor}
            allowClear
            placeholder="All Instructors"
          >
            {activeInstructors.map(inst => (
              <Option key={inst.instructor_id} value={inst.instructor_id}>{inst.instructor_name}</Option>
            ))}
          </Select>
        </Col>

        <Col xs={24} sm={12} md={4}>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Month / Date</span>
          <DatePicker 
            style={{ width: '100%' }}
            picker="month"
            value={filterDate}
            onChange={setFilterDate}
            format="MMMM YYYY"
          />
        </Col>
      </Row>
    </Card>
  );

  return (
    <div style={{ padding: 24, background: 'var(--panel-color)', minHeight: '80vh', color: 'var(--text-color)', borderRadius: 12, border: '1px solid var(--border-color)' }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ color: 'var(--text-color)', margin: 0 }}>Session Control Management</h2>
      </div>

      {/* Main Tabs */}
      <Tabs activeKey={activeTab} onChange={setActiveTab} type="card">
        {/* TAB 1: Instructor Session Matrix */}
        <Tabs.TabPane tab={<span><TableOutlined /> Instructor Session Matrix</span>} key="teacher-matrix">
          {matrixFilters}

          {/* Action Row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, color: 'var(--text-color)' }}>Instructor Session Matrix</h3>
            <Space>
              <Button icon={<DownloadOutlined />} onClick={() => downloadCSV('daily_session')}>
                Export CSV
              </Button>
              <Button icon={<FilePdfOutlined />} style={{ borderColor: '#ff4d4f', color: '#ff4d4f' }} onClick={downloadPDF}>
                Export PDF
              </Button>
            </Space>
          </div>

          {/* Instructor Session Matrix Table */}
          {matrixGroups.map(group => {
            const teacherMatrixData = group.data;
            return (
              <Card 
                key={group.clusterId || 'all'}
                style={{ 
                  background: 'var(--panel-color, #ffffff)', 
                  border: '1px solid var(--border-color)', 
                  borderRadius: 14, 
                  marginBottom: 20,
                  boxShadow: '0 2px 10px rgba(0,0,0,0.04)'
                }}
                bodyStyle={{ padding: 18 }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ width: 4, height: 18, borderRadius: 2, background: 'var(--primary-color)' }} />
                    <h4 style={{ color: 'var(--text-color)', margin: 0, fontSize: 15, fontWeight: 700 }}>
                      Instructor Session Matrix - {group.clusterName}
                    </h4>
                    <Tag color="blue" style={{ borderRadius: 10, fontSize: 11, padding: '0 8px', border: '1px solid rgba(22, 119, 255, 0.3)' }}>
                      {teacherMatrixData.teacherRows.length} Instructors
                    </Tag>
                    <Tag color="magenta" style={{ borderRadius: 10, fontSize: 11, padding: '0 8px', border: '1px solid rgba(235, 47, 150, 0.3)' }}>
                      {filterAcademicYear ? (academicYears.find(ay => String(ay.academic_year_id) === String(filterAcademicYear))?.academic_year || 'Academic Year') : 'All Years'}
                    </Tag>
                    <Tag color="cyan" style={{ borderRadius: 10, fontSize: 11, padding: '0 8px', border: '1px solid rgba(19, 194, 194, 0.3)' }}>
                      {filterStandard ? (standards.find(s => String(s.standard_id) === String(filterStandard))?.standard_name || 'Standard') : 'All Standards'}
                    </Tag>
                    <Tag color="green" style={{ borderRadius: 10, fontSize: 11, padding: '0 8px', border: '1px solid rgba(82, 196, 26, 0.3)' }}>
                      {filterDate ? filterDate.format('MMMM YYYY') : 'All Months'}
                    </Tag>
                    <Tag color="purple" style={{ borderRadius: 10, fontSize: 11, padding: '0 8px', border: '1px solid rgba(114, 46, 209, 0.3)' }}>
                      {teacherMatrixData.remainingClass} Total Remaining (Academic Year)
                    </Tag>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    Total Standards: <strong style={{ color: 'var(--text-color)' }}>{teacherMatrixData.sortedStds.length}</strong>
                  </div>
                </div>

                <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid var(--border-color)' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'center', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: 'var(--border-color, rgba(0, 0, 0, 0.04))', borderBottom: '2px solid var(--border-color)' }}>
                        <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: 'var(--text-color)', minWidth: 180 }}>
                          TEACHER / INSTRUCTOR
                        </th>
                        {teacherMatrixData.sortedStds.map(std => (
                          <th key={std.standard_id} style={{ padding: '10px 12px', fontWeight: 700, minWidth: 85 }}>
                            <Tag color="geekblue" style={{ fontSize: 12, padding: '2px 8px', borderRadius: 4, margin: 0 }}>
                              {std.standard_name.toUpperCase()}
                            </Tag>
                          </th>
                        ))}
                        <th style={{ padding: '10px 14px', fontWeight: 700, color: 'var(--text-color)', minWidth: 110, background: 'rgba(22, 119, 255, 0.08)' }}>
                          TOTAL SESSIONS
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {teacherMatrixData.teacherRows.map((t, idx) => (
                        <tr 
                          key={t.instructor_id} 
                          style={{ 
                            borderBottom: '1px solid var(--border-color)', 
                            background: idx % 2 === 0 ? 'transparent' : 'rgba(128,128,128,0.03)',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(128,128,128,0.08)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = idx % 2 === 0 ? 'transparent' : 'rgba(128,128,128,0.03)'}
                        >
                          <td style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--text-color)' }}>
                            <Space size={6}>
                              <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--primary-color)' }} />
                              {t.instructor_name}
                            </Space>
                          </td>
                          {teacherMatrixData.sortedStds.map(std => {
                            const liveCount = t.liveCounts[std.standard_id] || 0;
                            return (
                              <td key={std.standard_id} style={{ padding: '10px 12px' }}>
                                {liveCount > 0 ? (
                                  <span style={{ 
                                    display: 'inline-block',
                                    minWidth: 26,
                                    padding: '2px 6px',
                                    borderRadius: 6,
                                    background: 'rgba(82, 196, 26, 0.15)',
                                    color: '#52c41a',
                                    fontWeight: 700,
                                    fontSize: 13
                                  }}>
                                    {liveCount}
                                  </span>
                                ) : (
                                  <span style={{ color: 'var(--text-secondary)' }}>0</span>
                                )}
                              </td>
                            );
                          })}
                          <td style={{ padding: '10px 14px', fontWeight: 800, color: '#1677ff', background: 'rgba(22, 119, 255, 0.04)', fontSize: 14 }}>
                            {Object.values(t.liveCounts || {}).reduce((sum, c) => sum + (Number(c) || 0), 0)}
                          </td>
                        </tr>
                      ))}

                      {/* SUBTOTAL OF CLASS (Live only) */}
                      <tr style={{ borderTop: '2px double var(--border-color)', background: 'rgba(128, 128, 128, 0.05)', fontWeight: 700 }}>
                        <td style={{ padding: '10px 14px', textAlign: 'left', color: 'var(--text-color)' }}>
                          SUBTOTAL OF CLASS
                        </td>
                        {teacherMatrixData.sortedStds.map(std => (
                          <td key={std.standard_id} style={{ padding: '10px 12px' }}>
                            <Tag color="blue" style={{ fontSize: 12, fontWeight: 700, borderRadius: 4, margin: 0 }}>
                              {teacherMatrixData.stdSubtotals[std.standard_id] || 0}
                            </Tag>
                          </td>
                        ))}
                        <td style={{ padding: '10px 14px', fontWeight: 800, color: '#1677ff', background: 'rgba(22, 119, 255, 0.08)', fontSize: 14 }}>
                          {teacherMatrixData.totalLiveCount || 0}
                        </td>
                      </tr>

                      {/* TOTAL CLASS (Live only) */}
                      <tr style={{ background: 'rgba(24, 144, 255, 0.12)', fontWeight: 700 }}>
                        <td style={{ padding: '10px 14px', textAlign: 'left', color: '#1890ff', fontSize: 13 }}>
                          TOTAL CLASS
                        </td>
                        <td colSpan={teacherMatrixData.sortedStds.length + 1} style={{ padding: '10px 14px' }}>
                          <Tag color="processing" style={{ fontSize: 13, fontWeight: 800, padding: '2px 14px', borderRadius: 6 }}>
                            {teacherMatrixData.totalLiveCount} Live Sessions Conducted
                          </Tag>
                        </td>
                      </tr>

                      {/* TOTAL REMAINING SESSIONS */}
                      <tr style={{ background: 'rgba(114, 46, 209, 0.12)', fontWeight: 700 }}>
                        <td style={{ padding: '10px 14px', textAlign: 'left', color: '#722ed1', fontSize: 13 }}>
                          TOTAL REMAINING SESSIONS
                        </td>
                        <td colSpan={teacherMatrixData.sortedStds.length + 1} style={{ padding: '10px 14px' }}>
                          <Tag color="purple" style={{ fontSize: 13, fontWeight: 800, padding: '2px 14px', borderRadius: 6 }}>
                            {teacherMatrixData.remainingClass} Sessions Remaining (out of {teacherMatrixData.totalAllocatedSessions} Allocated for Academic Year)
                          </Tag>
                        </td>
                      </tr>

                      {/* CLASS CANCELLED */}
                      <tr style={{ background: 'rgba(255, 77, 79, 0.12)', fontWeight: 700 }}>
                        <td style={{ padding: '10px 14px', textAlign: 'left', color: '#ff4d4f', fontSize: 13 }}>
                          CLASS CANCELLED
                        </td>
                        <td colSpan={teacherMatrixData.sortedStds.length + 1} style={{ padding: '10px 14px' }}>
                          <Tag color="error" style={{ fontSize: 13, fontWeight: 800, padding: '2px 14px', borderRadius: 6 }}>
                            {teacherMatrixData.totalCancelledCount} Sessions Cancelled
                          </Tag>
                        </td>
                      </tr>

                      {/* RECORDED SESSIONS (separate, not counted in main matrix) */}
                      {teacherMatrixData.totalRecordedCount > 0 && (
                        <>
                          <tr style={{ background: 'rgba(22, 119, 255, 0.06)', borderTop: '2px dashed rgba(22, 119, 255, 0.3)' }}>
                            <td
                              colSpan={teacherMatrixData.sortedStds.length + 2}
                              style={{ padding: '7px 14px', textAlign: 'left', color: '#4096ff', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em' }}
                            >
                              📹 RECORDED SESSIONS (not counted in class total)
                            </td>
                          </tr>
                          {teacherMatrixData.teacherRows.filter(t => Object.values(t.recordedCounts).some(c => c > 0)).map((t, idx) => (
                            <tr
                              key={`rec-${t.instructor_id}`}
                              style={{ background: idx % 2 === 0 ? 'rgba(22,119,255,0.04)' : 'rgba(22,119,255,0.08)' }}
                            >
                              <td style={{ padding: '7px 14px', textAlign: 'left', fontWeight: 500, color: '#4096ff', fontStyle: 'italic' }}>
                                <Space>
                                  <Tag color="blue" style={{ fontSize: 10, borderRadius: 3, margin: 0 }}>REC</Tag>
                                  {t.instructor_name}
                                </Space>
                              </td>
                              {teacherMatrixData.sortedStds.map(std => {
                                const recCount = t.recordedCounts[std.standard_id] || 0;
                                return (
                                  <td key={std.standard_id} style={{ padding: '7px 12px' }}>
                                    {recCount > 0 ? (
                                      <Tag color="blue" style={{ fontSize: 11, fontWeight: 600, borderRadius: 4, margin: 0, opacity: 0.85 }}>
                                        {recCount}
                                      </Tag>
                                    ) : (
                                      <span style={{ color: 'rgba(64,150,255,0.2)' }}>—</span>
                                    )}
                                  </td>
                                );
                              })}
                              <td style={{ padding: '7px 14px', fontWeight: 700, color: '#4096ff' }}>
                                {Object.values(t.recordedCounts || {}).reduce((sum, c) => sum + (Number(c) || 0), 0)}
                              </td>
                            </tr>
                          ))}
                          <tr style={{ background: 'rgba(22, 119, 255, 0.1)', fontWeight: 700 }}>
                            <td style={{ padding: '8px 14px', textAlign: 'left', color: '#4096ff', fontSize: 12 }}>
                              TOTAL RECORDED
                            </td>
                            <td colSpan={teacherMatrixData.sortedStds.length + 1} style={{ padding: '8px 14px' }}>
                              <Tag color="blue" style={{ fontSize: 12, fontWeight: 800, padding: '2px 12px', borderRadius: 6 }}>
                                {teacherMatrixData.totalRecordedCount} Recorded Sessions
                              </Tag>
                            </td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            );
          })}
        </Tabs.TabPane>

        {/* TAB 2: Session Control Detailed Records */}
        <Tabs.TabPane tab={<span><CalendarOutlined /> Session Control Management</span>} key="session-control">
          {sessionFilters}

          {/* Action Row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, color: 'var(--text-color)' }}>Daily Session Matrix</h3>
            <Space wrap>
              <Button icon={<DownloadOutlined />} onClick={downloadCSVTemplate}>
                CSV Template
              </Button>
              <Upload accept=".csv" beforeUpload={handleCsvFileUpload} showUploadList={false}>
                <Button icon={<UploadOutlined />}>
                  Import CSV
                </Button>
              </Upload>
              <Button icon={<DownloadOutlined />} onClick={() => downloadCSV('daily_session')}>
                Export CSV
              </Button>
              <Button icon={<FilePdfOutlined />} style={{ borderColor: '#ff4d4f', color: '#ff4d4f' }} onClick={downloadPDF}>
                Export PDF
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal('daily_session')}>
                Add Daily Session
              </Button>
            </Space>
          </div>

          {/* Sessions Table */}
          <Table
            className="excel-table"
            dataSource={dailySessionsList}
            rowKey="daily_session_id"
            loading={dailySessionsLoading}
            pagination={{ pageSize: 15, showSizeChanger: true, pageSizeOptions: ['15', '30', '50', '100'] }}
            columns={dailySessionColumns}
            scroll={{ x: 'max-content' }}
          />
        </Tabs.TabPane>

        {/* TAB 2: Syllabus Progress Tracker (Academic Year-wise & Cluster-wise) */}
        <Tabs.TabPane tab={<span><BarChartOutlined /> Syllabus Progress</span>} key="syllabus-progress">
          <Card 
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-color)', borderRadius: 12, marginBottom: 16 }} 
            bodyStyle={{ padding: 16 }}
          >
            <Row gutter={[16, 16]} align="middle">
              {/* Filter 1: Academic Year */}
              <Col xs={24} sm={12} md={6}>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>1. Academic Year</span>
                <Select {...selectSearchProps}
                  style={{ width: '100%' }}
                  placeholder="All Academic Years"
                  allowClear
                  value={filterAyForProgress}
                  onChange={(val) => {
                    setFilterAyForProgress(val);
                    setFilterClusterForProgress(null);
                    setFilterStdForProgress(null);
                    setFilterSubForProgress(null);
                  }}
                >
                  {academicYears.map(ay => (
                    <Option key={ay.academic_year_id} value={ay.academic_year_id}>
                      {ay.academic_year} {ay.status === 'Active' || ay.status === 'ACTIVE' ? ' (Active)' : ''}
                    </Option>
                  ))}
                </Select>
              </Col>

              {/* Filter 2: Cluster */}
              <Col xs={24} sm={12} md={6}>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>2. Cluster</span>
                <Select {...selectSearchProps}
                  style={{ width: '100%' }}
                  placeholder="Select Cluster"
                  allowClear={!isStudio}
                  value={filterClusterForProgress}
                  onChange={(val) => {
                    setFilterClusterForProgress(val);
                    setFilterStdForProgress(null);
                    setFilterSubForProgress(null);
                  }}
                >
                  {availableClustersForProgress.map(c => (
                    <Option key={c.cluster_id} value={c.cluster_id}>
                      {c.cluster_name || `Cluster #${c.cluster_id}`} {c.cluster_code ? `(${c.cluster_code})` : ''}
                    </Option>
                  ))}
                </Select>
              </Col>

              {/* Filter 3: Standard */}
              <Col xs={24} sm={12} md={6}>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>3. Standard / Grade</span>
                <Select {...selectSearchProps}
                  style={{ width: '100%' }}
                  placeholder="Select Standard"
                  allowClear
                  value={filterStdForProgress}
                  onChange={(val) => {
                    setFilterStdForProgress(val);
                    setFilterSubForProgress(null);
                  }}
                >
                  {availableStandardsForProgress.map(s => (
                    <Option key={s.standard_id} value={s.standard_id}>
                      {s.standard_name} ({s.standard_medium || 'English'})
                    </Option>
                  ))}
                </Select>
              </Col>

              {/* Filter 4: Subject */}
              <Col xs={24} sm={12} md={6}>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>4. Subject (Optional)</span>
                <Select {...selectSearchProps}
                  style={{ width: '100%' }}
                  placeholder="All Subjects"
                  allowClear
                  value={filterSubForProgress}
                  onChange={(val) => setFilterSubForProgress(val)}
                >
                  {availableSubjectsForProgress.map(sub => (
                    <Option key={sub.subjects_id} value={sub.subjects_id}>
                      {sub.subjects_name} ({sub.subjects_medium || 'English'})
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>
            {(filterAyForProgress !== (getCurrentAcademicYear(academicYears)?.academic_year_id || null) || filterClusterForProgress || filterStdForProgress || filterSubForProgress) && (
              <div style={{ marginTop: 12, textAlign: 'right' }}>
                <Button size="small" icon={<ClearOutlined />} onClick={() => {
                  const activeAyId = getCurrentAcademicYear(academicYears)?.academic_year_id || null;
                  setFilterAyForProgress(activeAyId);
                  setFilterClusterForProgress(isStudio && availableClustersForProgress.length > 0 ? availableClustersForProgress[0].cluster_id : null);
                  setFilterStdForProgress(null);
                  setFilterSubForProgress(null);
                }}>Reset Progress Filters</Button>
              </div>
            )}
          </Card>

          {progressLoading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spin size="large" tip="Loading Syllabus Progress Data..." />
            </div>
          ) : clusterProgressGroups.length === 0 ? (
            <Card style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-color)', borderRadius: 12 }}>
              <Empty description="No syllabus progress data found for the selected filters." image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </Card>
          ) : (
            clusterProgressGroups.map((clusterGroup) => (
              <Card
                key={clusterGroup.clusterId}
                style={{
                  background: 'rgba(255, 255, 255, 0.02)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 14,
                  marginBottom: 24,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)'
                }}
                bodyStyle={{ padding: 18 }}
              >
                {/* Cluster Header Banner */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ width: 4, height: 20, borderRadius: 2, background: 'var(--primary-color)' }} />
                    <h4 style={{ color: 'var(--text-color)', margin: 0, fontSize: 16, fontWeight: 700 }}>
                      📍 {clusterGroup.clusterName} {clusterGroup.clusterCode ? `(${clusterGroup.clusterCode})` : ''}
                    </h4>
                    <Tag color="magenta" style={{ borderRadius: 10, fontSize: 11, padding: '0 8px', border: '1px solid rgba(235, 47, 150, 0.3)' }}>
                      {clusterGroup.academicYearName}
                    </Tag>
                    <Tag color="blue" style={{ borderRadius: 10, fontSize: 11, padding: '0 8px', border: '1px solid rgba(22, 119, 255, 0.3)' }}>
                      {clusterGroup.totalSubjects} Subjects
                    </Tag>
                    <Tag color="purple" style={{ borderRadius: 10, fontSize: 11, padding: '0 8px', border: '1px solid rgba(114, 46, 209, 0.3)' }}>
                      {clusterGroup.totalLessons} Lessons
                    </Tag>
                    <Tag color="cyan" style={{ borderRadius: 10, fontSize: 11, padding: '0 8px', border: '1px solid rgba(19, 194, 194, 0.3)' }}>
                      {clusterGroup.totalUsedSessions} / {clusterGroup.totalAllocatedSessions} Sessions Conducted
                    </Tag>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Cluster Progress: </span>
                      <strong style={{ fontSize: 14, color: clusterGroup.overallPercent === 100 ? '#52c41a' : 'var(--primary-color)' }}>
                        {clusterGroup.overallPercent}%
                      </strong>
                    </div>
                    <Progress
                      type="circle"
                      percent={clusterGroup.overallPercent}
                      width={38}
                      strokeWidth={10}
                      format={(pct) => `${pct}%`}
                      status={clusterGroup.overallPercent === 100 ? 'success' : 'normal'}
                    />
                  </div>
                </div>

                {/* Subjects Table for this Cluster */}
                <Table
                  className="excel-table"
                  dataSource={clusterGroup.subjects}
                  scroll={{ x: 'max-content' }}
                  columns={[
                    {
                      title: 'Standard',
                      dataIndex: 'standard_name',
                      key: 'standard_name',
                      width: 140,
                      render: (val, record) => (
                        <Tag color="geekblue" style={{ fontWeight: 700, padding: '2px 8px', borderRadius: 4 }}>
                          {val} ({record.standard_medium || 'English'})
                        </Tag>
                      )
                    },
                    {
                      title: 'Subject Name',
                      dataIndex: 'subjects_name',
                      key: 'subjects_name',
                      render: (val, record) => (
                        <span>
                          <strong style={{ fontSize: 13, color: 'var(--text-color)' }}>{val}</strong>
                          {record.subjects_code ? <Tag color="default" style={{ marginLeft: 6, fontSize: 11 }}>{record.subjects_code}</Tag> : ''}
                        </span>
                      )
                    },
                    {
                      title: 'Lessons Count',
                      key: 'lesson_count',
                      align: 'center',
                      width: 130,
                      render: (_, record) => (
                        <Tag color="blue" style={{ fontWeight: 600 }}>{record.progress.lessonCount} Lessons</Tag>
                      )
                    },
                    {
                      title: 'Allocated Sessions',
                      key: 'allocated_sessions',
                      align: 'center',
                      width: 150,
                      render: (_, record) => (
                        <Tag color="purple" style={{ fontWeight: 700 }}>{record.progress.totalAllocated} Sessions</Tag>
                      )
                    },
                    {
                      title: 'Used Sessions',
                      key: 'used_sessions',
                      align: 'center',
                      width: 160,
                      render: (_, record) => {
                        const isExceeded = record.progress.totalUsed > record.progress.totalAllocated;
                        return (
                          <Space direction="vertical" size={2} style={{ width: '100%', alignItems: 'center' }}>
                            <Tag color={isExceeded ? 'error' : 'cyan'} style={{ fontWeight: 700 }}>
                              {record.progress.totalUsed} Sessions
                            </Tag>
                            {isExceeded && (
                              <Tag color="error" style={{ fontSize: 11, fontWeight: 700, borderRadius: 4 }}>
                                ⚠ Exceeded by {record.progress.totalUsed - record.progress.totalAllocated}
                              </Tag>
                            )}
                          </Space>
                        );
                      }
                    },
                    {
                      title: 'Remaining',
                      key: 'remaining_sessions',
                      align: 'center',
                      width: 130,
                      render: (_, record) => (
                        <Tag color={record.progress.remaining === 0 ? 'green' : 'orange'} style={{ fontWeight: 600 }}>
                          {record.progress.remaining} Pending
                        </Tag>
                      )
                    },
                    {
                      title: 'Progress Status',
                      key: 'progress_status',
                      width: 220,
                      render: (_, record) => (
                        <Progress
                          percent={record.progress.percent}
                          size="small"
                          status={record.progress.percent === 100 ? 'success' : 'active'}
                        />
                      )
                    }
                  ]}
                  rowKey="subjects_id"
                  pagination={false}
                  summary={() => (
                    <Table.Summary.Row style={{ background: 'rgba(255, 255, 255, 0.04)', fontWeight: 'bold' }}>
                      <Table.Summary.Cell index={0} colSpan={3}>
                        <span style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          CLUSTER TOTALS ({clusterGroup.clusterName})
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="center">
                        <Tag color="blue" style={{ fontWeight: 800 }}>{clusterGroup.totalLessons} Lessons</Tag>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={4} align="center">
                        <Tag color="purple" style={{ fontWeight: 800 }}>{clusterGroup.totalAllocatedSessions} Allocated</Tag>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={5} align="center">
                        <Tag color="cyan" style={{ fontWeight: 800 }}>{clusterGroup.totalUsedSessions} Conducted</Tag>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={6} align="center">
                        <Tag color={clusterGroup.totalRemainingSessions === 0 ? 'green' : 'orange'} style={{ fontWeight: 800 }}>
                          {clusterGroup.totalRemainingSessions} Pending
                        </Tag>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={7}>
                        <Progress
                          percent={clusterGroup.overallPercent}
                          size="small"
                          status={clusterGroup.overallPercent === 100 ? 'success' : 'active'}
                        />
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                  expandable={{
                    expandedRowRender: (subjectRecord) => {
                      const subLessons = sortLessonsAscending(lessons.filter(l => String(l.subjects_id) === String(subjectRecord.subjects_id)));
                      if (subLessons.length === 0) {
                        return (
                          <div style={{ padding: '12px 24px', color: 'var(--text-secondary)', fontSize: 13 }}>
                            No lessons configured for this subject yet.
                          </div>
                        );
                      }
                      return (
                        <Card size="small" style={{ margin: '8px 16px', background: 'rgba(0,0,0,0.15)', borderColor: 'var(--border-color)', borderRadius: 8 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                            <h5 style={{ margin: 0, color: 'var(--primary-color)', fontSize: 13 }}>
                              📖 Lesson & Session Breakdown: {subjectRecord.subjects_name} ({clusterGroup.clusterName})
                            </h5>
                            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                              {subLessons.length} Total Lessons
                            </span>
                          </div>
                          <Table
                            size="small"
                            dataSource={subLessons}
                            rowKey="lessons_id"
                            pagination={false}
                            scroll={{ x: 'max-content' }}
                            columns={[
                              { title: '#', key: 'idx', render: (_, __, i) => i + 1, width: 50 },
                              {
                                title: 'Lesson / Chapter Name',
                                dataIndex: 'lesson_name',
                                key: 'lesson_name',
                                render: text => <strong style={{ color: 'var(--text-color)' }}>{text}</strong>
                              },
                              {
                                title: 'Allocated Sessions',
                                dataIndex: 'lesson_total_sessions',
                                key: 'lesson_total_sessions',
                                align: 'center',
                                width: 150,
                                render: count => <Tag color="purple">{count || 1} Sessions</Tag>
                              },
                              {
                                title: 'Used Sessions',
                                key: 'used_sessions',
                                align: 'center',
                                width: 170,
                                render: (_, lessonRecord) => {
                                  const { usedSessions } = getLessonProgressDataForCluster(lessonRecord.lessons_id, clusterGroup.clusterId);
                                  const alloc = Number(lessonRecord.lesson_total_sessions) || 1;
                                  const isExceeded = usedSessions > alloc;
                                  return (
                                    <Space direction="vertical" size={2} style={{ width: '100%', alignItems: 'center' }}>
                                      <Tag color={isExceeded ? 'error' : 'cyan'}>{usedSessions} Sessions</Tag>
                                      {isExceeded && (
                                        <Tag color="error" style={{ fontSize: 10, fontWeight: 700, borderRadius: 4 }}>
                                          ⚠ Exceeded by {usedSessions - alloc}
                                        </Tag>
                                      )}
                                    </Space>
                                  );
                                }
                              },
                              {
                                title: 'Status',
                                key: 'status',
                                align: 'center',
                                width: 140,
                                render: (_, lessonRecord) => {
                                  const { status } = getLessonProgressDataForCluster(lessonRecord.lessons_id, clusterGroup.clusterId);
                                  if (!status) return <Tag color="default">PENDING</Tag>;
                                  const upperStatus = status.toUpperCase();
                                  if (upperStatus === 'COMPLETED') {
                                    return <Tag color="success">COMPLETED</Tag>;
                                  } else if (upperStatus === 'PENDING') {
                                    return <Tag color="warning">PENDING</Tag>;
                                  } else if (upperStatus === 'IN_PROGRESS' || upperStatus === 'ONGOING') {
                                    return <Tag color="processing">IN PROGRESS</Tag>;
                                  } else {
                                    return <Tag color="blue">{upperStatus}</Tag>;
                                  }
                                }
                              },
                              {
                                title: 'Lesson Progress',
                                key: 'lesson_progress',
                                width: 160,
                                render: (_, lessonRecord) => {
                                  const { usedSessions } = getLessonProgressDataForCluster(lessonRecord.lessons_id, clusterGroup.clusterId);
                                  const alloc = Number(lessonRecord.lesson_total_sessions) || 1;
                                  const pct = Math.min(100, Math.round((usedSessions / alloc) * 100));
                                  return <Progress percent={pct} size="small" status={pct >= 100 ? 'success' : 'active'} />;
                                }
                              }
                            ]}
                          />
                        </Card>
                      );
                    }
                  }}
                />
              </Card>
            ))
          )}
        </Tabs.TabPane>
      </Tabs>

      {/* ── CSV Bulk Import Preview Modal ── */}
      <Modal
        title="Preview CSV Daily Sessions Import"
        open={isCsvModalOpen}
        onCancel={() => { setIsCsvModalOpen(false); setCsvParsedData([]); }}
        onOk={handleConfirmCsvUpload}
        confirmLoading={uploadingCsv}
        width={1100}
        okText="Import Daily Sessions"
        style={{ top: 20 }}
      >
        <p>Found <strong>{csvParsedData.length}</strong> daily session records in the uploaded file.</p>
        <Table
          dataSource={csvParsedData}
          rowKey={(rec, index) => index}
          size="small"
          bordered
          pagination={{ pageSize: 30, showSizeChanger: true, pageSizeOptions: ['30', '50', '100'] }}
          scroll={{ x: 'max-content' }}
          columns={[
            {
              title: 'SESSION TYPE',
              dataIndex: 'session_type',
              render: type => {
                const color = type === 'LIVE' ? 'green' : type === 'RECORDED' ? 'blue' : type === 'CANCELLED' ? 'volcano' : 'purple';
                return <Tag color={color}>{type || 'LIVE'}</Tag>;
              }
            },
            {
              title: 'SESSION DATE',
              dataIndex: 'daily_session_date',
              render: d => d ? dayjs(d).format('DD-MM-YYYY') : '—'
            },
            { title: 'ACADEMIC YEAR', dataIndex: 'academic_year_name', render: val => val || '—' },
            { title: 'CLUSTER', dataIndex: 'cluster_name', render: val => val || '—' },
            { title: 'STANDARD', dataIndex: 'standard_name', render: val => val || '—' },
            { title: 'SUBJECT', dataIndex: 'subjects_name', render: val => val || '—' },
            { title: 'INSTRUCTOR', dataIndex: 'instructor_name', render: val => val || '—' },
            { title: 'STAFF', dataIndex: 'staff_name', render: val => val || '—' },
            { title: 'LESSON', dataIndex: 'lesson_name', render: val => val || '—' },
            { title: 'TOPIC DETAILS / PART', dataIndex: 'daily_session_part', render: val => val || '—' },
            { title: 'START TIME', dataIndex: 'start_time', render: val => formatTime12h(val) },
            { title: 'END TIME', dataIndex: 'end_time', render: val => formatTime12h(val) },
            {
              title: 'LESSON STATUS',
              dataIndex: 'lesson_status',
              render: status => {
                const color = status === 'COMPLETED' ? 'success' : status === 'PENDING' ? 'warning' : status === 'CANCELLED' ? 'error' : 'processing';
                return <Tag color={color}>{status || 'COMPLETED'}</Tag>;
              }
            },
            {
              title: 'SESSION COUNT',
              dataIndex: 'session_count',
              render: count => <Tag color="blue">{count !== undefined && count !== null ? count : 1}</Tag>
            }
          ]}
        />
      </Modal>

      {/* Dynamic Modal Form for daily_session */}
      <Modal
        title={editingRecord ? `Edit ${modalType?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}` : `Create ${modalType?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}`}
        open={!!modalType}
        onCancel={() => setModalType(null)}
        footer={null}
        destroyOnClose
        width={760}
        style={{ top: 20 }}
      >
        <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
          {/* Daily Session Form */}
          {modalType === 'daily_session' && (
            <>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="session_type" label="Session Type" rules={[{ required: true }]}>
                    <Select {...selectSearchProps} placeholder="Select Session Type">
                      <Option value="LIVE">Live Session</Option>
                      <Option value="RECORDED">Recorded Session</Option>
                      <Option value="CANCELLED">Cancelled / No Class</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="daily_session_date" label="Session Date" rules={[{ required: true }]}>
                    <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="academic_year_id" label="Academic Year" rules={[{ required: true }]}>
                    <Select {...selectSearchProps} placeholder="Select Academic Year" onChange={handleFormAcademicYearChange}>
                      {academicYears.filter(ay => ay.status === 'Active' || ay.status === 'ACTIVE' || !ay.status).map(ay => (
                        <Option key={ay.academic_year_id} value={ay.academic_year_id}>{ay.academic_year}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="cluster_id" label="Cluster" rules={[{ required: true, message: 'Please select a cluster' }]}>
                    <Select {...selectSearchProps} placeholder="Select Cluster" onChange={handleFormClusterChange} allowClear>
                      {formAvailableClusters.map(c => (
                        <Option key={c.cluster_id} value={c.cluster_id}>{c.cluster_name} ({c.cluster_code})</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="standard_id" label="Standard" rules={[{ required: true, message: 'Please select a standard' }]}>
                    <Select {...selectSearchProps}
                      placeholder={modalType === 'daily_session' && !formClusterId ? "Select Cluster first" : "Select Standard"}
                      onChange={handleFormStandardChange}
                      disabled={modalType === 'daily_session' && !formClusterId}
                      allowClear
                    >
                      {formAvailableStandards.map(s => (
                        <Option key={s.standard_id} value={s.standard_id}>{s.standard_name} ({s.standard_medium})</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="subjects_id" label="Subject">
                    <Select {...selectSearchProps} 
                      placeholder={formStandardId ? "Select Subject" : "Please select Standard first"} 
                      onChange={handleFormSubjectChange} 
                      disabled={!formStandardId}
                      allowClear
                    >
                      {formAvailableSubjects.map(sub => (
                        <Option key={sub.subjects_id} value={sub.subjects_id}>{sub.subjects_name}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="instructor_id" label="Instructor" rules={isClassType ? [{ required: true, message: 'Please select an instructor' }] : []}>
                    <Select 
                      {...selectSearchProps} 
                      placeholder={isInstructorStaffDisabled ? "Allowed only for Tech Problem / Heavy Rain" : "Select Instructor"} 
                      disabled={isInstructorStaffDisabled}
                      allowClear
                    >
                      {availableInstructorsForForm.map(i => (
                        <Option key={i.instructor_id} value={i.instructor_id}>{i.instructor_name}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="staff_id" label="Staff">
                    <Select 
                      {...selectSearchProps} 
                      placeholder={isInstructorStaffDisabled ? "Allowed only for Tech Problem / Heavy Rain" : "Select Staff Member"} 
                      disabled={isInstructorStaffDisabled}
                      allowClear
                    >
                      {activeStaffList.map(s => (
                        <Option key={s.staff_id} value={s.staff_id}>
                          {s.staff_name} {s.staff_type ? `(${s.staff_type})` : ''}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              {isClassType && isLessonAlreadyCompleted && (
                <Alert
                  type="error"
                  showIcon
                  message="Lesson Already Completed"
                  description={
                    completedLessonInfo?.daily_session_date
                      ? `This Lesson / Topic is already marked as COMPLETED for this cluster and academic year (Completed on ${dayjs(completedLessonInfo.daily_session_date).format('DD-MM-YYYY')}${completedLessonInfo.daily_session_part ? ` in ${completedLessonInfo.daily_session_part}` : ''}). You cannot create or schedule another session for this completed lesson.`
                      : "This Lesson / Topic is already marked as COMPLETED for this cluster and academic year. You cannot create or schedule another session for this completed lesson."
                  }
                  style={{ marginBottom: 16 }}
                />
              )}

              <Row gutter={16}>
                {isClassType && (
                  <Col span={12}>
                    <Form.Item name="lessons_id" label="Lesson / Topic">
                      <Select {...selectSearchProps} placeholder="Select Lesson" onChange={handleFormLessonChange} allowClear>
                        {formAvailableLessons.map(l => (
                          <Option key={l.lessons_id} value={l.lessons_id}>{l.lesson_name}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}
                <Col span={isClassType ? 12 : 24}>
                  <Form.Item 
                    name="daily_session_part" 
                    label={isClassType ? "Topic Details / Part (e.g. Part-1)" : "Reason / Note (Select 'Technical Problem' or 'Heavy Rain' to credit instructor)"}
                    tooltip={isClassType ? "Auto-calculated part number based on Academic Year and Cluster for this Lesson / Topic" : undefined}
                  >
                    {sessionTypeValue === 'CANCELLED' ? (
                      <AutoComplete
                        placeholder="Type or select reason (e.g. Technical Problem or Heavy Rain)"
                        options={[
                          { value: 'Technical Problem' },
                          { value: 'Heavy Rain' },
                          { value: 'Instructor Absent' },
                          { value: 'Holiday' }
                        ]}
                        filterOption={(inputValue, option) =>
                          option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                        }
                      />
                    ) : isClassType ? (
                      <Input 
                        readOnly 
                        style={{ backgroundColor: 'rgba(255,255,255,0.06)', cursor: 'not-allowed' }}
                        placeholder="Auto-generated (e.g. Part-1)" 
                      />
                    ) : (
                      <Input placeholder="e.g. KPS EXAM IN STUDIO Institution / CANCELLED" />
                    )}
                  </Form.Item>
                </Col>
              </Row>

              {isClassType && (
                <>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="start_time" label="Start Time">
                        <TimePicker style={{ width: '100%' }} format="hh:mm A" use12Hours />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="end_time" label="End Time">
                        <TimePicker style={{ width: '100%' }} format="hh:mm A" use12Hours />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="lesson_status" label="Lesson Status">
                        <Select {...selectSearchProps} placeholder="Select Status (Can be empty)" allowClear>
                          <Option value="COMPLETED">COMPLETED</Option>
                          <Option value="PENDING">PENDING</Option>
                          <Option value="CANCELLED">CANCELLED</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="session_count" label="Session Count" initialValue={1}>
                        <InputNumber style={{ width: '100%' }} min={0.5} max={10} step={0.5} />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )}
              <div style={{ textAlign: 'right', marginTop: 16 }}>
                <Space>
                  <Button onClick={() => setModalType(null)}>Cancel</Button>
                  <Button type="primary" htmlType="submit" loading={modalLoading} disabled={modalLoading || (isLessonAlreadyCompleted && isClassType)}>Submit</Button>
                </Space>
              </div>
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default SessionManagementPage;
